import { AlertCircle, Check, ChevronRight, ClipboardList, Copy, CreditCard, Loader, Package, Printer, Search, Settings, Star, Truck, X } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    listOrders, getOrderDetail, acknowledgeOrder, acknowledgeAllOrders, shipOrder, cancelOrderLines, enrichOrderImage,
    getShippingEstimates, buyShippingLabel, fetchSwwLabelBlob, downloadShippedOrderLabel, getDeliverBy
} from "../services/orderService";
import { withPageRetry, getItemsFeesBatch, mergeItemFeeKey, getItemsMetadata, getLivePriceIncentives } from "../services/catalogService";
import { downloadPackingSlip } from "../utils/packingSlip";
import FeeUpdateConfirmModal from "../components/common/FeeUpdateConfirmModal";
import { getReturnAddress } from "../services/wfsShipmentService";

const money = (v) => `$${(v || 0).toFixed(2)}`;
const PAGE_SIZE = 25;
const FROM_ADDRESS_KEY = "sww_ship_from_address";
const PKG_DIMS_BY_SKU_QTY_KEY = "sww_pkg_dims_by_sku_qty";
const PKG_PRESETS_KEY = "sww_pkg_presets";
const LAST_ORDERS_TAB_KEY = "orders_last_tab";

// Some orders (seen on WFS, but harmless to apply everywhere) have
// duplicate order_history ROWS for the exact same purchase_order_id +
// line_number - a backend sync artifact, not a real second line. The
// detail popup already renders lines keyed by line_number, so React
// silently collapses these duplicates there (which is why the popup
// shows "1" while the list, counting raw rows, showed "2"). Dedupe here
// the same way so the list and popup always agree.
const dedupeByLineNumber = (rows) => {
    const seen = new Set();
    const result = [];
    for (const r of rows) {
        const key = `${r.purchase_order_id}::${r.line_number}`;
        if (seen.has(key)) continue;
        seen.add(key);
        result.push(r);
    }
    return result;
};

const walmartItemUrl = (l) =>
    l.wpid
        ? `https://www.walmart.com/ip/${l.wpid}`
        : `https://www.walmart.com/search?q=${encodeURIComponent(l.product_name || "")}`;

const truncateTitle = (title, max = 40) => {
    if (!title) return "";
    return title.length > max ? title.slice(0, max).trimEnd() + "…" : title;
};

// Some synced order data (and older locally-saved addresses) carry a
// 3-letter or spelled-out country instead of the 2-letter ISO code
// Walmart's shipping APIs expect. Normalize to the 2-letter form wherever
// a country value enters or leaves this component.
const COUNTRY_CODE_MAP = {
    USA: "US",
    "UNITED STATES": "US",
    "UNITED STATES OF AMERICA": "US",
};

const normalizeCountryCode = (code) => {
    if (!code) return "US";
    const upper = code.trim().toUpperCase();
    return COUNTRY_CODE_MAP[upper] || upper;
};

const openBlobInNewTab = (blob) => {
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 30000);
};

// Barcodes may come in with spaces/dashes from a paste - strip to digits only.
const normalizeScanCode = (v) => (v || "").replace(/\D/g, "");

// UPC (12) and GTIN (14) for the same product often differ only by leading
// zero padding - "contains either direction" catches both, same trick used
// on the WFS packing screen.
const scanCodesMatch = (a, b) => {
    if (!a || !b) return false;
    return a === b || a.includes(b) || b.includes(a);
};

function makePickListDistortionCurve(amount) {
    const samples = 44100;
    const curve = new Float32Array(samples);
    for (let i = 0; i < samples; i++) {
        const x = (i * 2) / samples - 1;
        curve[i] = ((3 + amount) * x * 20 * (Math.PI / 180)) / (Math.PI + amount * Math.abs(x));
    }
    return curve;
}

const playPickErrorBeep = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const shaper = ctx.createWaveShaper();
        osc.type = "square";
        osc.frequency.value = 220;
        shaper.curve = makePickListDistortionCurve(60);
        shaper.oversample = "4x";
        const now = ctx.currentTime;
        gain.gain.setValueAtTime(0.001, now);
        gain.gain.exponentialRampToValueAtTime(0.8, now + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.8, now + 0.2);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
        osc.connect(gain); gain.connect(shaper); shaper.connect(ctx.destination);
        osc.start(now); osc.stop(now + 0.25);
        osc.onended = () => ctx.close();
    } catch { /* audio unavailable - fail silently */ }
};

const playPickSuccessDing = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const shaper = ctx.createWaveShaper();
        shaper.curve = makePickListDistortionCurve(15);
        shaper.oversample = "4x";
        shaper.connect(ctx.destination);
        const playTone = (freq, startTime, duration) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(freq, startTime);
            gain.gain.setValueAtTime(0.001, startTime);
            gain.gain.exponentialRampToValueAtTime(0.8, startTime + 0.015);
            gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
            osc.connect(gain); gain.connect(shaper);
            osc.start(startTime); osc.stop(startTime + duration);
        };
        const now = ctx.currentTime;
        playTone(1046.5, now, 0.16);
        playTone(1568, now + 0.1, 0.22);
        setTimeout(() => ctx.close(), 400);
    } catch { /* audio unavailable - fail silently */ }
};

// Small inline "copy this value" button - flashes a checkmark for ~1.5s
// after a successful copy, then reverts to the copy icon. Disabled (and
// silently a no-op) on empty values so there's nothing blank to paste.
const CopyIconButton = ({ value, copied, onCopy, title = "Copy" }) => (
    <button
        type="button"
        onClick={() => value && onCopy()}
        disabled={!value}
        title={value ? title : "Nothing to copy"}
        className="flex-shrink-0 p-1.5 text-gray-300 hover:text-teal-600 disabled:opacity-30 disabled:hover:text-gray-300 transition"
    >
        {copied ? <Check size={12} className="text-teal-600" /> : <Copy size={12} />}
    </button>
);

// Walmart's per-line raw payload usually carries the customer's postal
// address under shippingInfo.postalAddress. Not every stored line has it
// (e.g. this WFS example row doesn't), so we fall back to the flatter
// buyer_name/state/postal_code columns we already sync.
const deriveShipToFromOrder = (order, lines) => {
    // Walmart's shippingInfo/postalAddress sit at the ORDER level, but
    // raw_line only ever stores a single orderLine (see _flatten_order_lines
    // on the backend) - it never has shippingInfo on it, so `postal` here
    // was always null and every field silently fell back to whatever
    // flattened column happened to exist. The backend already parses the
    // full customer postal address per line into shipping_address - use
    // that instead.
    const postal = lines?.[0]?.shipping_address || null;
    return {
        name: postal?.name || order?.buyer_name || "",
        addressLine1: postal?.address1 || postal?.addressLine1 || "",
        addressLine2: postal?.address2 || postal?.addressLine2 || "",
        city: postal?.city || "",
        state: postal?.state || order?.state || "",
        postalCode: postal?.postalCode || order?.postal_code || "",
        country: normalizeCountryCode(postal?.country || order?.country),
    };
};

const ProductImage = ({ src, fallback = "📦" }) => {
    const [errored, setErrored] = useState(false);
    const [hoverPos, setHoverPos] = useState(null);
    const imgRef = useRef(null);

    if (!src || errored) return <span className="text-2xl">{fallback}</span>;

    const handleEnter = () => {
        const rect = imgRef.current?.getBoundingClientRect();
        if (!rect) return;
        const previewSize = 200;
        const spaceOnRight = window.innerWidth - rect.right;
        const left = spaceOnRight > previewSize + 16 ? rect.right + 8 : rect.left - previewSize - 8;
        const top = Math.min(Math.max(rect.top + rect.height / 2 - previewSize / 2, 8), window.innerHeight - previewSize - 8);
        setHoverPos({ top, left });
    };

    return (
        <>
            <img ref={imgRef} src={src} alt="" onError={() => setErrored(true)}
                onMouseEnter={handleEnter} onMouseLeave={() => setHoverPos(null)}
                className="w-11 h-11 object-contain rounded cursor-zoom-in flex-shrink-0" />
            {hoverPos && (
                <div className="fixed z-50 bg-white border border-gray-200 rounded-xl shadow-2xl p-2 pointer-events-none"
                    style={{ top: hoverPos.top, left: hoverPos.left }}>
                    <img src={src} alt="" className="rounded-lg object-contain" style={{ width: 200, height: 200 }} />
                </div>
            )}
        </>
    );
};

// Same stacked-avatar treatment as BatchImageStack (Batches) and
// ShipmentImageStack (Shipments) - shows up to `max` distinct product
// images overlapping, with a "+N" badge for anything beyond that, so a
// multi-item order is recognizable at a glance instead of just showing
// the first line's image with a bare count badge.
const ORDER_STACK_PREVIEW_MAX = 3;

const OrderImageStack = ({ group, max = 4 }) => {
    const withImages = group.filter((l) => l.image_url);
    const shown = withImages.length > 0 ? withImages.slice(0, max) : group.slice(0, max);
    const remaining = group.length - shown.length;

    const [hoverPos, setHoverPos] = useState(null); // {top, left} in viewport coords, or null when not hovering
    const stackRef = useRef(null);

    const previewItems = withImages.length > 0 ? withImages : group;
    const previewShown = previewItems.slice(0, ORDER_STACK_PREVIEW_MAX);
    const previewRemaining = previewItems.length - previewShown.length;

    const handleEnter = () => {
        const rect = stackRef.current?.getBoundingClientRect();
        if (!rect) return;
        const previewSize = 80;
        const gap = 8;
        const totalWidth = previewShown.length * previewSize + (previewShown.length - 1) * gap + (previewRemaining > 0 ? gap + previewSize : 0);
        const spaceOnRight = window.innerWidth - rect.left;
        const left = spaceOnRight > totalWidth + 16 ? rect.left : Math.max(8, window.innerWidth - totalWidth - 16);
        const top = Math.max(rect.top - previewSize - 12, 8);
        setHoverPos({ top, left });
    };

    return (
        <div ref={stackRef} className="relative flex-shrink-0" onMouseEnter={handleEnter} onMouseLeave={() => setHoverPos(null)}>
            <div className="flex items-center -space-x-2">
                {shown.map((l, i) => (
                    <div key={l.id || i} className="w-9 h-9 rounded-full border-2 border-white shadow-sm bg-gray-100 overflow-hidden flex items-center justify-center"
                        style={{ zIndex: shown.length - i }}>
                        {l.image_url ? <img src={l.image_url} alt="" className="w-full h-full object-cover" /> : <span className="text-sm">📦</span>}
                    </div>
                ))}
                {remaining > 0 && (
                    <div className="w-9 h-9 rounded-full border-2 border-white shadow-sm bg-gray-700 text-white flex items-center justify-center text-[10px] font-bold" style={{ zIndex: 0 }}>
                        +{remaining}
                    </div>
                )}
            </div>

            {hoverPos && group.length > 1 && (
                // position: fixed escapes the order card's own layout/clipping,
                // same pattern as ProductImage's zoom preview.
                <div className="fixed z-50 bg-white border border-gray-200 rounded-xl shadow-2xl p-2 flex items-center gap-2 pointer-events-none"
                    style={{ top: hoverPos.top, left: hoverPos.left }}>
                    {previewShown.map((l, i) => (
                        <div key={l.id || i} className="w-20 h-20 rounded-lg bg-gray-50 overflow-hidden flex items-center justify-center flex-shrink-0" title={l.product_name}>
                            {l.image_url ? <img src={l.image_url} alt="" className="w-full h-full object-contain" /> : <Package size={20} className="text-gray-300" />}
                        </div>
                    ))}
                    {previewRemaining > 0 && (
                        <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 text-gray-500 font-bold text-sm">
                            +{previewRemaining} more
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const OrderCardSkeleton = () => (
    <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3 animate-pulse">
        <div className="w-11 h-11 rounded bg-gray-200 flex-shrink-0" />
        <div className="flex-1 space-y-2">
            <div className="h-3 bg-gray-200 rounded w-1/3" />
            <div className="h-2.5 bg-gray-100 rounded w-2/3" />
            <div className="h-2.5 bg-gray-100 rounded w-1/4" />
        </div>
        <div className="w-16 h-5 bg-gray-200 rounded-full flex-shrink-0" />
    </div>
);

const STATUS_STYLES = {
    Created: "bg-gray-100 text-gray-500",
    Acknowledged: "bg-blue-50 text-blue-600",
    Shipped: "bg-teal-50 text-teal-700",
    Delivered: "bg-green-50 text-green-600",
    Cancelled: "bg-red-50 text-red-600",
    Mixed: "bg-yellow-50 text-yellow-700",
    "Bought Label - Mark Shipped": "bg-purple-50 text-purple-700",
};
const StatusBadge = ({ status }) => (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_STYLES[status] || "bg-gray-100 text-gray-500"}`}>
        {status || "Unknown"}
    </span>
);

// Must match Walmart's cancellationReason enum exactly - see
// https://developer.walmart.com/api/us/mp/orders#operation/cancelOrderLines
const CANCEL_REASON_OPTIONS = [
    { value: "SELLER_CANCEL_OUT_OF_STOCK", label: "Out of stock" },
    { value: "SELLER_CANCEL_PRICING_ERROR", label: "Pricing error" },
    { value: "SELLER_CANCEL_ADDRESS_NOT_SERVICEABLE", label: "Address not serviceable" },
    { value: "CUSTOMER_REQUESTED_SELLER_TO_CANCEL", label: "Customer requested cancellation" },
    { value: "SELLER_CANCEL_FRAUD_STOP_SHIPMENT", label: "Suspected fraud" },
];

const PACKAGE_TYPE_OPTIONS = [
    { value: "CUSTOM_PACKAGE", label: "Custom Box (enter dimensions)" },
    { value: "FEDEX_ENVELOPE", label: "FedEx Envelope" },
    { value: "FEDEX_PAK", label: "FedEx Pak" },
    { value: "USPS_FLAT_RATE_ENVELOPE", label: "USPS Flat Rate Envelope" },
    { value: "USPS_FLAT_RATE_BOX", label: "USPS Flat Rate Box" },
    { value: "USPS_LARGE_FLAT_RATE_BOX", label: "USPS Large Flat Rate Box" },
    { value: "OTHER", label: "Other (type manually)" },
];

const loadSavedFromAddress = () => {
    try {
        const raw = localStorage.getItem(FROM_ADDRESS_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

// Per-SKU+quantity remembered package dims, so re-shipping the same
// item/qty combo doesn't require re-typing the box size every time.
const pkgKey = (sku, qty) => `${sku}::${qty}`;

const loadPkgDimsMap = () => {
    try {
        const raw = localStorage.getItem(PKG_DIMS_BY_SKU_QTY_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const savePkgDimsForSkuQty = (sku, qty, entry) => {
    try {
        const map = loadPkgDimsMap();
        map[pkgKey(sku, qty)] = entry;
        localStorage.setItem(PKG_DIMS_BY_SKU_QTY_KEY, JSON.stringify(map));
    } catch { /* non-fatal */ }
};

// User-saved named box presets, selectable from the package type dropdown.
const loadPkgPresets = () => {
    try {
        const raw = localStorage.getItem(PKG_PRESETS_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
};

const savePkgPresetToStorage = (preset) => {
    try {
        const list = loadPkgPresets();
        list.push(preset);
        localStorage.setItem(PKG_PRESETS_KEY, JSON.stringify(list));
        return list;
    } catch {
        return loadPkgPresets();
    }
};

// Whether buying a label immediately marks the order Shipped, or leaves it
// Acknowledged (with tracking/carrier already attached) until the seller
// marks it shipped manually - e.g. to get a head start on tomorrow's
// orders without notifying Walmart/the customer yet.
const SHIP_TIMING_PREF_KEY = "sww_ship_timing_pref"; // "immediate" | "deferred" | absent = ask each time

const loadShipTimingPref = () => {
    try {
        return localStorage.getItem(SHIP_TIMING_PREF_KEY);
    } catch {
        return null;
    }
};

const saveShipTimingPref = (value) => {
    try {
        if (value) localStorage.setItem(SHIP_TIMING_PREF_KEY, value);
        else localStorage.removeItem(SHIP_TIMING_PREF_KEY);
    } catch { /* non-fatal */ }
};

// True once a label has been bought (backend already persisted
// tracking_number/carrier from sww-buy-label) but the order hasn't been
// reported as Shipped to Walmart yet.
const hasUnshippedLabel = (line) =>
    line.status === "Acknowledged" && !!line.sww_label_tracking_number && !!line.sww_label_carrier;

// Display-only status label - the real stored status is still
// "Acknowledged" (Walmart has no "label bought" status of its own), but
// showing that as-is here would look identical to any other order the
// seller hasn't dealt with yet. Surface the distinction so a
// deferred-ship order is unmistakable at a glance.
const LABEL_BOUGHT_STATUS = "Bought Label - Mark Shipped";
const lineDisplayStatus = (line) => hasUnshippedLabel(line) ? LABEL_BOUGHT_STATUS : line.status;

// True when the ZIP includes a "+4" suffix (e.g. "74023-1771") - this gets
// trimmed to the plain 5-digit form before being sent to Walmart's Create
// Label endpoint (see cleanZip in handleBuyLabel), so the seller should
// know their exact entry isn't what actually gets submitted.
const isZipPlusFour = (zip) => /^\d{5}-\d{3,4}$/.test((zip || "").trim());

// Normalizes a carrier string to Walmart's expected carrierShortName
// format (lowercase, no spaces) — same logic used server-side.
const carrierToShortName = (carrier) => (carrier || "").trim().toLowerCase().replace(/\s+/g, "");

// For lines that already have a bought (but possibly unshipped) SWW
// label — we already have carrier/tracking_number synced locally from
// sww-buy-label, so this calls the SWW label endpoint directly instead of
// going through raw_line (which only gets a trackingInfo block once
// Walmart processes an actual Shipped status).
const DownloadSwwLabelButton = ({ carrier, trackingNumber }) => {
    const [state, setState] = useState("idle"); // idle | loading | notfound

    const handleClick = async (e) => {
        e.stopPropagation();
        setState("loading");
        try {
            const blob = await fetchSwwLabelBlob(carrierToShortName(carrier), trackingNumber);
            openBlobInNewTab(blob);
            setState("idle");
        } catch {
            setState("notfound");
            setTimeout(() => setState("idle"), 2500);
        }
    };

    return (
        <button onClick={handleClick} disabled={state === "loading"}
            className="text-[10px] font-bold text-teal-600 hover:text-teal-700 disabled:opacity-50">
            {state === "loading" ? "..." : state === "notfound" ? "NOT FOUND" : "Download Label"}
        </button>
    );
};

const LoadDeliverByButton = ({ purchaseOrderId }) => {
    const [state, setState] = useState("idle"); // idle | loading | notfound
    const [date, setDate] = useState(null);

    const handleClick = async (e) => {
        e.stopPropagation();
        setState("loading");
        try {
            const d = await getDeliverBy(purchaseOrderId);
            if (!d.estimated_delivery_date) throw new Error("missing");
            setDate(new Date(Number(d.estimated_delivery_date)));
            setState("idle");
        } catch {
            setState("notfound");
            setTimeout(() => setState("idle"), 2500);
        }
    };

    if (date) return <span className="font-semibold text-blue-600">Deliver by {date.toLocaleDateString()}</span>;
    return (
        <span role="button" onClick={handleClick}
            className="font-bold text-teal-600 hover:text-teal-700 cursor-pointer">
            {state === "loading" ? "..." : state === "notfound" ? "NOT FOUND" : "Load Deliver By"}
        </span>
    );
};

// Incentive lookups are cached in localStorage for 3 days (best incentive per
// SKU only, to keep it small) so buttons fill in automatically on later visits.
const INCENTIVE_CACHE_KEY = "orders_incentive_cache_v1";
const INCENTIVE_CACHE_TTL_MS = 3 * 24 * 60 * 60 * 1000;
const INCENTIVES_UPDATED_EVENT = "orders-incentives-updated";

let incentiveMemCache = null; // { savedAt, bySku }
const readIncentiveCache = () => {
    if (!incentiveMemCache) {
        try { incentiveMemCache = JSON.parse(localStorage.getItem(INCENTIVE_CACHE_KEY) || "null"); } catch { incentiveMemCache = null; }
    }
    if (!incentiveMemCache?.savedAt || Date.now() - incentiveMemCache.savedAt > INCENTIVE_CACHE_TTL_MS) return null;
    return incentiveMemCache.bySku || null;
};

const writeIncentiveCache = (bySku) => {
    incentiveMemCache = { savedAt: Date.now(), bySku };
    try { localStorage.setItem(INCENTIVE_CACHE_KEY, JSON.stringify(incentiveMemCache)); } catch { /* quota - memory cache still works */ }
    window.dispatchEvent(new Event(INCENTIVES_UPDATED_EVENT)); // tells every button on screen to update
};

const pickBestIncentive = (list) => {
    const withTarget = (list || []).filter((i) => i.target_price != null);
    if (withTarget.length === 0) return null;
    const best = withTarget.reduce((a, b) => (b.target_price < a.target_price ? b : a));
    return { target_price: best.target_price, reduced_referral_fee: best.reduced_referral_fee, query_status: best.query_status };
};

// One shared fetch; uses the 3-day cache unless force = true.
let incentivesPromise = null;
const loadIncentivesOnce = (force = false) => {
    if (!force) {
        const cached = readIncentiveCache();
        if (cached) return Promise.resolve(cached);
    }
    if (!incentivesPromise) {
        incentivesPromise = getLivePriceIncentives()
            .then((d) => {
                const bySku = {};
                Object.entries(d.by_sku || {}).forEach(([sku, list]) => {
                    const best = pickBestIncentive(list);
                    if (best) bySku[sku] = best;
                });
                writeIncentiveCache(bySku);
                return bySku;
            })
            .finally(() => { incentivesPromise = null; });
    }
    return incentivesPromise;
};

// span (not button) so it can sit inside the clickable order cards.
// result: undefined = not loaded yet, null = no incentive, object = incentive
const LoadIncentiveButton = ({ sku, soldPrice }) => {
    const fromCache = () => { const c = readIncentiveCache(); return c ? (c[sku] || null) : undefined; };
    const [result, setResult] = useState(fromCache);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        const onUpdate = () => setResult(fromCache());
        window.addEventListener(INCENTIVES_UPDATED_EVENT, onUpdate);
        return () => window.removeEventListener(INCENTIVES_UPDATED_EVENT, onUpdate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sku]);

    const run = async (e, force = false) => {
        e.stopPropagation();
        setLoading(true);
        setFailed(false);
        try {
            const bySku = await loadIncentivesOnce(force);
            setResult(bySku[sku] || null);
        } catch {
            setFailed(true);
            setTimeout(() => setFailed(false), 2500);
        } finally {
            setLoading(false);
        }
    };

    const refreshIcon = (
        <span role="button" title="Refresh incentive data" onClick={(e) => run(e, true)}
            className="ml-1 font-bold text-teal-600 hover:text-teal-700 cursor-pointer">
            {loading ? "…" : failed ? "!" : "↻"}
        </span>
    );

    if (result) {
        const diff = soldPrice != null ? soldPrice - result.target_price : null;
        return (
            <span className="font-semibold text-gray-600">
                Sold {soldPrice != null ? money(soldPrice) : "—"} · Incentive {money(result.target_price)}
                {diff != null && (
                    <span className={diff <= 0 ? "text-green-600" : "text-orange-500"}>
                        {" "}({diff > 0 ? "+" : ""}{money(diff).replace("$-", "-$")})
                    </span>
                )}
                {result.reduced_referral_fee != null && <span className="text-gray-400"> · {result.reduced_referral_fee}% fee</span>}
                {result.query_status && <span className="text-gray-400"> · {result.query_status}</span>}
                {refreshIcon}
            </span>
        );
    }
    if (result === null) {
        return <span className="text-gray-400">No incentive{refreshIcon}</span>;
    }
    return null; // not loaded yet - use "Load All Incentives" at the top
};

const LoadAllIncentivesButton = () => {
    const [state, setState] = useState("idle"); // idle | loading | done | error
    const handleClick = async () => {
        setState("loading");
        try {
            await loadIncentivesOnce(true);
            setState("done");
        } catch {
            setState("error");
        }
        setTimeout(() => setState("idle"), 2000);
    };
    return (
        <button onClick={handleClick} disabled={state === "loading"}
            title="Fetches incentives for every SKU and caches them for 3 days"
            className="px-4 py-2 border border-teal-200 text-teal-700 bg-teal-50 rounded-lg text-sm font-bold hover:bg-teal-100 transition disabled:opacity-50">
            {state === "loading" ? "Loading..." : state === "done" ? "✓ Incentives Loaded" : state === "error" ? "Failed - Retry" : "Load All Incentives"}
        </button>
    );
};

const PackingSlipButton = ({ order, lines }) => (
    <button onClick={(e) => { e.stopPropagation(); downloadPackingSlip(order, lines); }}
        className="text-[10px] font-bold text-gray-500 hover:text-gray-700">
        Packing Slip
    </button>
);

// Simple, no stored short-name: pulls carrier + tracking from raw_line at
// click time via the backend. If Walmart can't return the label (or the
// carrier name doesn't map cleanly), just says NOT FOUND.
const DownloadLabelInlineButton = ({ purchaseOrderId, lineNumber }) => {
    const [state, setState] = useState("idle"); // idle | loading | notfound

    const handleClick = async (e) => {
        e.stopPropagation();
        setState("loading");
        try {
            const blob = await downloadShippedOrderLabel(purchaseOrderId, lineNumber);
            openBlobInNewTab(blob);
            setState("idle");
        } catch {
            setState("notfound");
            setTimeout(() => setState("idle"), 2500);
        }
    };

    return (
        <button onClick={handleClick} disabled={state === "loading"}
            className="text-[10px] font-bold text-teal-600 hover:text-teal-700 disabled:opacity-50">
            {state === "loading" ? "..." : state === "notfound" ? "NOT FOUND" : "Download Label"}
        </button>
    );
};

const BuyLabelPanel = ({ order, lines, shippableLines, selectedLines, onBought }) => {
    const [fromAddress, setFromAddress] = useState(() => loadSavedFromAddress() || {
        contactName: "", companyName: "", addressLine1: "", addressLine2: "",
        city: "", state: "", postalCode: "", country: "US", phone: "", email: "",
    });
    const [saveFromAddress, setSaveFromAddress] = useState(true);
    const [loadingFromAddress, setLoadingFromAddress] = useState(true);

    const [toAddress, setToAddress] = useState(() => deriveShipToFromOrder(order, lines));

    // Tracks which "Ship To" field was just copied, so only that field's
    // icon flashes a checkmark before reverting.
    const [copiedField, setCopiedField] = useState(null);
    const copyValue = (field, value) => {
        if (!value) return;
        navigator.clipboard.writeText(value).then(() => {
            setCopiedField(field);
            setTimeout(() => setCopiedField((c) => (c === field ? null : c)), 1500);
        }).catch(() => { /* clipboard permission denied - non-fatal */ });
    };
    const fullShipToAddress = [
        toAddress.name,
        toAddress.addressLine1,
        toAddress.addressLine2,
        [toAddress.city, toAddress.state, toAddress.postalCode].filter(Boolean).join(", "),
    ].filter(Boolean).join("\n");

    const [packageTypeChoice, setPackageTypeChoice] = useState("CUSTOM_PACKAGE");
    const [customPackageType, setCustomPackageType] = useState("");
    const [dims, setDims] = useState({ length: "", width: "", height: "", weight: "", dimUnit: "IN", weightUnit: "LB" });
    const [presets, setPresets] = useState(() => loadPkgPresets());
    const autoFilledKeyRef = useRef(null);
    const linesToShip = shippableLines.filter((l) => selectedLines.has(l.line_number));
    // Walmart's estimate call only has one blanket "addOns: true/false"
    // flag - it can't compute cost for just one type. So we always ask
    // for both when either is wanted, then let the seller choose exactly
    // which one(s) to actually purchase at label-creation time via the
    // addOns array. Changing either checkbox invalidates any estimates
    // already on screen, since the totals shown depend on this selection.
    const [wantSignature, setWantSignature] = useState(false);
    const [wantInsurance, setWantInsurance] = useState(false);
    const [hasBattery, setHasBattery] = useState(false);
    const [hazmat, setHazmat] = useState(false);

    const toggleSignature = () => {
        setWantSignature((v) => !v);
        setEstimates(null);
        setSelectedEstimateKey(null);
    };
    const toggleInsurance = () => {
        setWantInsurance((v) => !v);
        setEstimates(null);
        setSelectedEstimateKey(null);
    };

    // Pulls this estimate's per-unit add-on charge from Walmart's response
    // (est.addOns: [{ name: "SIGNATURE"|"INSURANCE", charge: {amount}, ... }]).
    // Returns null if that add-on wasn't priced for this particular service
    // (some services don't support signature/insurance at all).
    const getAddOnCharge = (est, name) => {
        const addOn = (est.addOns || []).find((a) => a.name === name);
        return addOn ? (addOn.charge?.amount ?? null) : null;
    };

    const estimateTotal = (est) => {
        let total = est.estimatedRate?.amount || 0;
        if (wantSignature) total += getAddOnCharge(est, "SIGNATURE") || 0;
        if (wantInsurance) total += getAddOnCharge(est, "INSURANCE") || 0;
        return total;
    };

    const [estimates, setEstimates] = useState(null);
    const [estimateError, setEstimateError] = useState("");
    const [loadingEstimates, setLoadingEstimates] = useState(false);
    const [selectedEstimateKey, setSelectedEstimateKey] = useState(null);

    const [buying, setBuying] = useState(false);
    const [buyError, setBuyError] = useState("");

    const [showShipTimingPrompt, setShowShipTimingPrompt] = useState(false);
    const [rememberShipTimingChoice, setRememberShipTimingChoice] = useState(true);
    const [showShipSettingsMenu, setShowShipSettingsMenu] = useState(false);

    // Pull the seller's saved WFS return address as the ship-from default -
    // only fills fields the user hasn't already saved locally, so a manual
    // override from a previous label still wins.
    useEffect(() => {
        getReturnAddress()
            .then((d) => {
                const addr = d.address;
                if (!addr) return;
                setFromAddress((prev) => ({
                    ...prev,
                    addressLine1: prev.addressLine1 || addr.address_line1 || "",
                    addressLine2: prev.addressLine2 || addr.address_line2 || "",
                    city: prev.city || addr.city || "",
                    state: prev.state || addr.state_code || "",
                    postalCode: prev.postalCode || addr.postal_code || "",
                    country: normalizeCountryCode(prev.country || addr.country_code),
                }));
            })
            .catch(() => { /* no saved return address yet - leave blank for manual entry */ })
            .finally(() => setLoadingFromAddress(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-fill the last-used box dims for this exact SKU+quantity combo,
    // once per combo per panel session.
    useEffect(() => {
        if (linesToShip.length !== 1) return;
        const line = linesToShip[0];
        const key = pkgKey(line.sku, line.quantity);
        if (autoFilledKeyRef.current === key) return;
        autoFilledKeyRef.current = key;
        const saved = loadPkgDimsMap()[key];
        if (!saved) return;
        setPackageTypeChoice(saved.packageTypeChoice || "CUSTOM_PACKAGE");
        setCustomPackageType(saved.customPackageType || "");
        setDims(saved.dims || dims);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linesToShip.map((l) => `${l.sku}:${l.quantity}`).join(",")]);

    const setFrom = (field, val) => setFromAddress((a) => ({ ...a, [field]: val }));
    const setTo = (field, val) => setToAddress((a) => ({ ...a, [field]: val }));
    const setDim = (field, val) => setDims((d) => ({ ...d, [field]: val }));

    // Saves the box config currently on screen the moment the seller leaves
    // a dimension field (onBlur), not just after a successful rate lookup -
    // so partial edits aren't lost if they never click "Get Shipping Rates".
    // Same single-line SKU+qty key as the existing estimate-time save.
    const saveCurrentDimsForLine = () => {
        if (linesToShip.length !== 1) return;
        const line = linesToShip[0];
        savePkgDimsForSkuQty(line.sku, line.quantity, {
            packageTypeChoice, customPackageType, dims,
        });
    };

    const activePreset = packageTypeChoice.startsWith("PRESET_")
        ? presets.find((p) => p.id === packageTypeChoice.slice(7))
        : null;
    const effectivePackageType = activePreset
        ? (activePreset.basePackageType === "OTHER" ? activePreset.customPackageTypeValue : activePreset.basePackageType)
        : (packageTypeChoice === "OTHER" ? customPackageType.trim() : packageTypeChoice);
    const needsDims = (activePreset ? activePreset.basePackageType : packageTypeChoice) === "CUSTOM_PACKAGE";

    const fromComplete = fromAddress.addressLine1 && fromAddress.city && fromAddress.state && fromAddress.postalCode && fromAddress.contactName && fromAddress.phone;
    const toComplete = toAddress.addressLine1 && toAddress.city && toAddress.state && toAddress.postalCode;
    const dimsComplete = dims.weight && (!needsDims || (dims.length && dims.width && dims.height));
    const canGetEstimates = fromComplete && toComplete && dimsComplete && effectivePackageType && linesToShip.length > 0;

    const boxItems = linesToShip.map((l) => ({
        sku: l.sku, quantity: l.quantity, lineNumber: String(l.line_number),
    }));

    const boxDimensions = {
        boxWeight: parseFloat(dims.weight) || 0,
        boxWeightUnit: dims.weightUnit,
        ...(needsDims ? {
            boxLength: parseFloat(dims.length) || 0,
            boxWidth: parseFloat(dims.width) || 0,
            boxHeight: parseFloat(dims.height) || 0,
            boxDimensionUnit: dims.dimUnit,
        } : {}),
    };

    const handleGetEstimates = async () => {
        setLoadingEstimates(true);
        setEstimateError("");
        setEstimates(null);
        setSelectedEstimateKey(null);
        try {
            const body = {
                purchaseOrderId: order.purchase_order_id,
                packageType: effectivePackageType,
                boxDimensions,
                boxItems,
                fromAddress: {
                    postalCode: fromAddress.postalCode, countryCode: normalizeCountryCode(fromAddress.country),
                    addressLines: [fromAddress.addressLine1, fromAddress.addressLine2].filter(Boolean),
                    city: fromAddress.city, state: fromAddress.state,
                },
                toAddress: {
                    postalCode: toAddress.postalCode, countryCode: normalizeCountryCode(toAddress.country),
                    addressLines: [toAddress.addressLine1, toAddress.addressLine2].filter(Boolean),
                    city: toAddress.city, state: toAddress.state,
                },
                addOns: wantSignature || wantInsurance,
                includeServicesNotMeetingDeliveryPromise: true,
            };
            const resp = await getShippingEstimates(order.purchase_order_id, body);
            const list = resp?.data?.estimates || [];
            setEstimates(list);
            if (list.length === 0) setEstimateError(resp?.data?.alertMessage || "No shipping options returned for this address/package.");
            else if (linesToShip.length === 1) {
                // Remember these dims against this exact SKU+qty for next time.
                savePkgDimsForSkuQty(linesToShip[0].sku, linesToShip[0].quantity, {
                    packageTypeChoice, customPackageType, dims,
                });
            }
        } catch (e) {
            setEstimateError(e.message || "Failed to fetch shipping estimates");
        } finally {
            setLoadingEstimates(false);
        }
    };

    const handleBuyLabel = async (shipNow) => {
        // Create label has no toAddress field at all - Walmart resolves the
        // customer's ship-to internally from purchaseOrderId. toAddress
        // above is only used for the estimate call.
        const est = (estimates || []).find((e) => `${e.name}-${e.carrierName}` === selectedEstimateKey);
        if (!est) return;
        setBuying(true);
        setBuyError("");
        try {
            if (saveFromAddress) {
                try { localStorage.setItem(FROM_ADDRESS_KEY, JSON.stringify(fromAddress)); } catch { /* non-fatal */ }
            }
            // Walmart's Create Label endpoint appears to be strict about
            // empty strings on optional fields (companyName, email) - omit
            // them entirely rather than sending "" when blank, same pattern
            // already used elsewhere in this app for other Walmart write
            // endpoints. Also strip any ZIP+4 suffix down to plain 5-digit,
            // since the estimate call (which succeeded) only ever sent
            // 5-digit ZIPs and this is the one structural difference between
            // the two payloads worth ruling out.
            const cleanZip = (zip) => (zip || "").split("-")[0].trim();
            const fromAddressPayload = {
                contactName: fromAddress.contactName,
                addressLine1: fromAddress.addressLine1,
                city: fromAddress.city, state: fromAddress.state,
                postalCode: cleanZip(fromAddress.postalCode),
                country: normalizeCountryCode(fromAddress.country), phone: fromAddress.phone,
                ...(fromAddress.companyName ? { companyName: fromAddress.companyName } : {}),
                ...(fromAddress.addressLine2 ? { addressLine2: fromAddress.addressLine2 } : {}),
                ...(fromAddress.email ? { email: fromAddress.email } : {}),
            };

            const body = {
                purchaseOrderId: order.purchase_order_id,
                packageType: effectivePackageType,
                boxDimensions,
                boxItems,
                fromAddress: fromAddressPayload,
                carrierName: est.carrierName,
                carrierServiceType: est.name,
                addOns: [
                    ...(wantSignature ? ["SIGNATURE"] : []),
                    ...(wantInsurance ? ["INSURANCE"] : []),
                ],
                ...(hasBattery ? { hasBattery: true } : {}),
                ...(hazmat ? { hazmat: true } : {}),
            };
            const resp = await buyShippingLabel(order.purchase_order_id, body);
            const data = resp?.data;
            if (!data?.trackingNo) throw new Error("Walmart didn't return a tracking number");

            if (shipNow) {
                await shipOrder(order.purchase_order_id, linesToShip.map((l) => ({
                    line_number: l.line_number, quantity: l.quantity,
                    carrier: data.carrierFullName || data.carrierName, tracking_number: data.trackingNo,
                })));
            }
            // When deferred, sww-buy-label already persisted tracking_number/
            // carrier onto these lines server-side - status stays Acknowledged
            // until the seller marks it shipped later (see handleMarkLineShipped
            // in OrderDetailModal).

            // Automatically update the stored fulfillment cost estimate to
            // the REAL per-unit cost we just paid - no confirmation prompt.
            try {
                const totalQty = linesToShip.reduce((s, l) => s + (l.quantity || 0), 0);
                // Use the full total (base rate + any add-ons actually
                // purchased), not just the base rate - otherwise buying
                // Signature/Insurance silently undercounts the real
                // per-unit fulfillment cost synced below.
                const perUnitCost = totalQty > 0 ? estimateTotal(est) / totalQty : 0;
                const uniqueCatalogIds = [...new Set(linesToShip.map((l) => l.catalog_item_id).filter(Boolean))];
                if (uniqueCatalogIds.length > 0 && perUnitCost > 0) {
                    await Promise.all(uniqueCatalogIds.map((cid) => mergeItemFeeKey(cid, "fulfillment_cost", perUnitCost, "label")));
                }
            } catch { /* non-fatal - fee sync never blocks the actual shipping flow */ }

            try {
                const blob = await fetchSwwLabelBlob(data.carrierName, data.trackingNo);
                openBlobInNewTab(blob);
            } catch { /* label still bought - just couldn't auto-open it */ }

            onBought();
        } catch (e) {
            setBuyError(e.message || "Failed to buy shipping label");
        } finally {
            setBuying(false);
        }
    };

    const handleBuyLabelClick = () => {
        const pref = loadShipTimingPref();
        if (pref === "immediate" || pref === "deferred") {
            handleBuyLabel(pref === "immediate");
            return;
        }
        setShowShipTimingPrompt(true);
    };

    return (
        <div className="border border-gray-200 rounded-xl p-4">
            <p className="text-xs font-bold text-gray-600 mb-3 flex items-center gap-1.5"><CreditCard size={14} /> Buy a Shipping Label via Walmart</p>

            {buyError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 mb-3 text-xs text-red-700 flex items-start gap-2">
                    <AlertCircle size={13} className="flex-shrink-0 mt-0.5" /> {buyError}
                </div>
            )}

            <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                    <p className="text-[11px] font-bold text-gray-500 uppercase mb-1.5 flex items-center gap-1.5">
                        Ship From {loadingFromAddress && <Loader size={11} className="animate-spin text-gray-300" />}
                    </p>
                    <div className="space-y-1.5">
                        <input placeholder="Contact name" value={fromAddress.contactName} onChange={(e) => setFrom("contactName", e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                        <input placeholder="Company (optional)" value={fromAddress.companyName} onChange={(e) => setFrom("companyName", e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                        <input placeholder="Address line 1" value={fromAddress.addressLine1} onChange={(e) => setFrom("addressLine1", e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                        <input placeholder="Address line 2 (optional)" value={fromAddress.addressLine2} onChange={(e) => setFrom("addressLine2", e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                        <div className="grid grid-cols-3 gap-1.5">
                            <input placeholder="City" value={fromAddress.city} onChange={(e) => setFrom("city", e.target.value)}
                                className="col-span-2 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            <input placeholder="State" value={fromAddress.state} onChange={(e) => setFrom("state", e.target.value.toUpperCase())} maxLength={2}
                                className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                        </div>
                        <input placeholder="ZIP" value={fromAddress.postalCode} onChange={(e) => setFrom("postalCode", e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                        {isZipPlusFour(fromAddress.postalCode) && (
                            <p className="text-[10px] text-gray-400 -mt-1">
                                We'll use just the 5-digit ZIP ({fromAddress.postalCode.split("-")[0]}) when buying the label.
                            </p>
                        )}
                        <input placeholder="Phone (required)" value={fromAddress.phone} onChange={(e) => setFrom("phone", e.target.value)}
                            className={`w-full border rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500 ${!fromAddress.phone ? "border-orange-300 bg-orange-50" : "border-gray-200"}`} />
                        <label className="flex items-center gap-1.5 text-[11px] text-gray-500 pt-0.5">
                            <input type="checkbox" checked={saveFromAddress} onChange={(e) => setSaveFromAddress(e.target.checked)} className="accent-teal-600" />
                            Remember this ship-from address
                        </label>
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <p className="text-[11px] font-bold text-gray-500 uppercase">Ship To (customer)</p>
                        <button type="button" onClick={() => copyValue("all", fullShipToAddress)}
                            disabled={!fullShipToAddress}
                            className="text-[10px] font-semibold text-teal-600 hover:text-teal-700 disabled:opacity-30 flex items-center gap-1">
                            {copiedField === "all" ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy all</>}
                        </button>
                    </div>
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-1">
                            <input placeholder="Name" value={toAddress.name} onChange={(e) => setTo("name", e.target.value)}
                                className="flex-1 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            <CopyIconButton value={toAddress.name} copied={copiedField === "name"} onCopy={() => copyValue("name", toAddress.name)} />
                        </div>
                        <div className="flex items-center gap-1">
                            <input placeholder="Address line 1" value={toAddress.addressLine1} onChange={(e) => setTo("addressLine1", e.target.value)}
                                className="flex-1 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            <CopyIconButton value={toAddress.addressLine1} copied={copiedField === "addressLine1"} onCopy={() => copyValue("addressLine1", toAddress.addressLine1)} />
                        </div>
                        <div className="flex items-center gap-1">
                            <input placeholder="Address line 2 (optional)" value={toAddress.addressLine2} onChange={(e) => setTo("addressLine2", e.target.value)}
                                className="flex-1 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            <CopyIconButton value={toAddress.addressLine2} copied={copiedField === "addressLine2"} onCopy={() => copyValue("addressLine2", toAddress.addressLine2)} />
                        </div>
                        <div className="flex items-center gap-1">
                            <div className="grid grid-cols-3 gap-1.5 flex-1">
                                <input placeholder="City" value={toAddress.city} onChange={(e) => setTo("city", e.target.value)}
                                    className="col-span-2 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                                <input placeholder="State" value={toAddress.state} onChange={(e) => setTo("state", e.target.value.toUpperCase())} maxLength={2}
                                    className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            </div>
                            <CopyIconButton value={[toAddress.city, toAddress.state].filter(Boolean).join(", ")} copied={copiedField === "cityState"}
                                onCopy={() => copyValue("cityState", [toAddress.city, toAddress.state].filter(Boolean).join(", "))} />
                        </div>
                        <div className="flex items-center gap-1">
                            <input placeholder="ZIP" value={toAddress.postalCode} onChange={(e) => setTo("postalCode", e.target.value)}
                                className="flex-1 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            <CopyIconButton value={toAddress.postalCode} copied={copiedField === "postalCode"} onCopy={() => copyValue("postalCode", toAddress.postalCode)} />
                        </div>
                        {!toAddress.addressLine1 && (
                            <p className="text-[11px] text-orange-500 flex items-start gap-1 pt-0.5">
                                <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
                                No street address on file for this order yet - enter it manually (name/state/ZIP were pulled in automatically where available).
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="mb-4">
                <p className="text-[11px] font-bold text-gray-500 uppercase mb-1.5">Package</p>
                <div className="grid grid-cols-2 gap-2 mb-2">
                    <select value={packageTypeChoice} onChange={(e) => {
                        const val = e.target.value;
                        setPackageTypeChoice(val);
                        if (val.startsWith("PRESET_")) {
                            const p = presets.find((pr) => pr.id === val.slice(7));
                            if (p) {
                                setCustomPackageType(p.basePackageType === "OTHER" ? p.customPackageTypeValue : "");
                                setDims(p.dims);
                            }
                        }
                    }}
                        onBlur={saveCurrentDimsForLine}
                        className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500 bg-white">
                        {presets.length > 0 && (
                            <optgroup label="Saved Presets">
                                {presets.map((p) => <option key={p.id} value={`PRESET_${p.id}`}>{p.name}</option>)}
                            </optgroup>
                        )}
                        {PACKAGE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {packageTypeChoice === "OTHER" && (
                        <input placeholder="Walmart packageTypeShortName" value={customPackageType} onChange={(e) => setCustomPackageType(e.target.value)}
                            onBlur={saveCurrentDimsForLine}
                            className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                    )}
                </div>
                <div className="grid grid-cols-5 gap-1.5">
                    {needsDims && (
                        <>
                            <input type="number" step="0.1" placeholder="Length" value={dims.length} onChange={(e) => setDim("length", e.target.value)}
                                onBlur={saveCurrentDimsForLine}
                                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            <input type="number" step="0.1" placeholder="Width" value={dims.width} onChange={(e) => setDim("width", e.target.value)}
                                onBlur={saveCurrentDimsForLine}
                                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            <input type="number" step="0.1" placeholder="Height" value={dims.height} onChange={(e) => setDim("height", e.target.value)}
                                onBlur={saveCurrentDimsForLine}
                                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            <select value={dims.dimUnit} onChange={(e) => setDim("dimUnit", e.target.value)}
                                className="border border-gray-200 rounded-lg px-1.5 py-1.5 text-xs focus:outline-none focus:border-teal-500 bg-white">
                                <option value="IN">in</option><option value="FT">ft</option><option value="CM">cm</option>
                            </select>
                        </>
                    )}
                    <input type="number" step="0.1" placeholder="Weight" value={dims.weight} onChange={(e) => setDim("weight", e.target.value)}
                        onBlur={saveCurrentDimsForLine}
                        className={`border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500 ${needsDims ? "" : "col-span-4"}`} />
                    <select value={dims.weightUnit} onChange={(e) => setDim("weightUnit", e.target.value)}
                        className="border border-gray-200 rounded-lg px-1.5 py-1.5 text-xs focus:outline-none focus:border-teal-500 bg-white">
                        <option value="LB">lb</option><option value="KG">kg</option><option value="OZ">oz</option>
                    </select>
                </div>
                <button type="button" onClick={() => {
                    const name = window.prompt("Name this box preset (e.g. \"Small Poly Mailer\"):");
                    if (!name || !name.trim()) return;
                    const preset = {
                        id: `${Date.now()}`,
                        name: name.trim(),
                        basePackageType: packageTypeChoice === "OTHER" ? "OTHER" : packageTypeChoice,
                        customPackageTypeValue: packageTypeChoice === "OTHER" ? customPackageType.trim() : "",
                        dims,
                    };
                    setPresets(savePkgPresetToStorage(preset));
                }} disabled={!dims.weight || (needsDims && (!dims.length || !dims.width || !dims.height))}
                    className="text-[11px] text-teal-600 hover:text-teal-700 font-semibold mt-1.5 disabled:opacity-40 disabled:hover:text-teal-600">
                    + Save current size as preset
                </button>
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                    <label className="flex items-center gap-1.5 text-[11px] text-gray-500">
                        <input type="checkbox" checked={wantSignature} onChange={toggleSignature}
                            disabled={!!estimates} className="accent-teal-600 disabled:opacity-50" />
                        Require Signature
                    </label>
                    <label className="flex items-center gap-1.5 text-[11px] text-gray-500">
                        <input type="checkbox" checked={wantInsurance} onChange={toggleInsurance}
                            disabled={!!estimates} className="accent-teal-600 disabled:opacity-50" />
                        Add Insurance
                    </label>
                    {(wantSignature || wantInsurance) && estimates && (
                        <span className="text-[10px] text-gray-400">Use "← Change" below to adjust add-ons</span>
                    )}
                </div>
                <div className="flex items-center gap-4 mt-1.5">
                    <label className="flex items-center gap-1.5 text-[11px] text-gray-500">
                        <input type="checkbox" checked={hasBattery} onChange={(e) => setHasBattery(e.target.checked)}
                            className="accent-teal-600" />
                        Contains Battery
                    </label>
                    <label className="flex items-center gap-1.5 text-[11px] text-gray-500">
                        <input type="checkbox" checked={hazmat} onChange={(e) => setHazmat(e.target.checked)}
                            className="accent-teal-600" />
                        Hazardous Material
                    </label>
                </div>
            </div>

            {estimateError && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5 mb-3 text-xs text-orange-700 flex items-start gap-2">
                    <AlertCircle size={13} className="flex-shrink-0 mt-0.5" /> {estimateError}
                </div>
            )}

            {!estimates && (
                <button onClick={handleGetEstimates} disabled={!canGetEstimates || loadingEstimates}
                    className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition disabled:opacity-40 flex items-center justify-center gap-1.5">
                    {loadingEstimates ? <><Loader size={14} className="animate-spin" /> Getting Rates...</> : "Get Shipping Rates"}
                </button>
            )}

            {estimates && estimates.length > 0 && (
                <div>
                    <div className="space-y-1.5 mb-3 max-h-56 overflow-y-auto">
                        {estimates.map((est) => {
                            const key = `${est.name}-${est.carrierName}`;
                            const selected = selectedEstimateKey === key;
                            const sigCharge = wantSignature ? getAddOnCharge(est, "SIGNATURE") : null;
                            const insCharge = wantInsurance ? getAddOnCharge(est, "INSURANCE") : null;
                            const sigUnavailable = wantSignature && sigCharge === null;
                            const insUnavailable = wantInsurance && insCharge === null;
                            // A service that can't carry a wanted add-on must not be
                            // selectable - otherwise Buy Label still sends that add-on
                            // to Walmart for a service that doesn't support it, either
                            // getting rejected or silently shipping without the
                            // protection the seller thought they were buying.
                            const blocked = sigUnavailable || insUnavailable;
                            return (
                                <label key={key}
                                    className={`flex items-center justify-between p-2.5 rounded-lg border-2 transition text-xs ${blocked ? "border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed"
                                        : selected ? "border-teal-500 bg-teal-50 cursor-pointer" : "border-gray-200 hover:border-gray-300 cursor-pointer"
                                        }`}>
                                    <div className="flex items-center gap-2">
                                        <input type="radio" checked={selected} disabled={blocked}
                                            onChange={() => !blocked && setSelectedEstimateKey(key)} className="accent-teal-600" />
                                        <div>
                                            <p className="font-bold text-gray-700">{est.displayName || est.name}</p>
                                            <p className="text-gray-400">{est.carrierDisplayName || est.carrierName}{est.deliveryDate ? ` · Est. delivery ${new Date(est.deliveryDate).toLocaleDateString()}` : ""}</p>
                                            {(wantSignature || wantInsurance) && (
                                                <p className={blocked ? "text-red-400 mt-0.5" : "text-gray-400 mt-0.5"}>
                                                    Base {money(est.estimatedRate?.amount)}
                                                    {wantSignature && (sigUnavailable ? " · Signature not supported" : ` · Signature +${money(sigCharge)}`)}
                                                    {wantInsurance && (insUnavailable ? " · Insurance not supported" : ` · Insurance +${money(insCharge)}`)}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <p className="font-bold text-gray-700">{money(estimateTotal(est))}</p>
                                </label>
                            );
                        })}
                    </div>
                    <div className="flex gap-2 relative">
                        <button onClick={() => { setEstimates(null); setSelectedEstimateKey(null); }}
                            className="px-3 py-2 border border-gray-200 rounded-lg text-xs font-bold text-gray-500 hover:bg-gray-50 transition">
                            ← Change Package/Address
                        </button>
                        <button onClick={handleBuyLabelClick} disabled={!selectedEstimateKey || buying}
                            className="flex-1 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-bold hover:bg-teal-700 transition disabled:opacity-40 flex items-center justify-center gap-1.5">
                            {buying
                                ? <><Loader size={14} className="animate-spin" /> Buying Label...</>
                                : loadShipTimingPref() === "deferred" ? "Buy Label (Ship Later)" : "Buy Label & Mark Shipped"}
                        </button>
                        <button type="button" onClick={() => setShowShipSettingsMenu((v) => !v)} title="When to mark orders shipped"
                            className="px-3 py-2.5 border border-gray-200 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition">
                            <Settings size={14} />
                        </button>
                        {showShipSettingsMenu && (
                            <div className="absolute right-0 bottom-full mb-2 bg-white border border-gray-200 rounded-xl shadow-xl p-2 w-72 z-10">
                                <p className="text-[11px] font-bold text-gray-400 uppercase px-2 pb-1.5">When buying a label</p>
                                {[
                                    { key: "immediate", label: "Always mark shipped immediately" },
                                    { key: "deferred", label: "Always buy the label only - I'll mark shipped later" },
                                    { key: null, label: "Ask me every time" },
                                ].map((opt) => (
                                    <button key={opt.label} type="button"
                                        onClick={() => { saveShipTimingPref(opt.key); setShowShipSettingsMenu(false); }}
                                        className={`w-full text-left px-2 py-1.5 rounded-lg text-xs hover:bg-gray-50 transition ${loadShipTimingPref() === opt.key ? "font-bold text-teal-600" : "text-gray-600"}`}>
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showShipTimingPrompt && (
                <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowShipTimingPrompt(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
                        <p className="text-sm font-bold text-gray-800 mb-1">Mark as shipped now?</p>
                        <p className="text-xs text-gray-500 mb-4">
                            You can buy and print the label now and mark it shipped whenever you're ready - handy for getting a head start.
                        </p>
                        <div className="space-y-2 mb-3">
                            <button onClick={() => {
                                setShowShipTimingPrompt(false);
                                if (rememberShipTimingChoice) saveShipTimingPref("immediate");
                                handleBuyLabel(true);
                            }} className="w-full py-2.5 bg-teal-600 text-white rounded-lg text-sm font-bold hover:bg-teal-700 transition">
                                Buy Label & Mark Shipped Now
                            </button>
                            <button onClick={() => {
                                setShowShipTimingPrompt(false);
                                if (rememberShipTimingChoice) saveShipTimingPref("deferred");
                                handleBuyLabel(false);
                            }} className="w-full py-2.5 border-2 border-gray-200 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-50 transition">
                                Buy Label Only - I'll Mark Shipped Later
                            </button>
                        </div>
                        <label className="flex items-center gap-1.5 text-[11px] text-gray-500">
                            <input type="checkbox" checked={rememberShipTimingChoice}
                                onChange={(e) => setRememberShipTimingChoice(e.target.checked)} className="accent-teal-600" />
                            Remember my choice (changeable anytime via the gear icon)
                        </label>
                    </div>
                </div>
            )}
        </div>
    );
};

const OrderDetailModal = ({ order, onClose, onChanged }) => {
    const purchaseOrderId = order.purchase_order_id;
    const [lines, setLines] = useState(null);
    const [selectedLines, setSelectedLines] = useState(new Set());
    const [acking, setAcking] = useState(false);
    const [shipping, setShipping] = useState(false);
    const [shipMode, setShipMode] = useState(null);
    const [carrier, setCarrier] = useState("");
    const [trackingNumber, setTrackingNumber] = useState("");
    const [error, setError] = useState("");
    const imageEnrichAttemptedRef = useRef(new Set());

    // Per-line cancel: which line's confirm prompt is open, and which
    // line_number is actively being submitted (disables just that row's
    // button rather than the whole modal).
    const [confirmCancelLine, setConfirmCancelLine] = useState(null);
    const [cancelReason, setCancelReason] = useState("SELLER_CANCEL_OUT_OF_STOCK");
    const [cancellingLine, setCancellingLine] = useState(null);

    // line_number of the row being marked shipped, or "__all__" for the
    // bulk action, or null when idle.
    const [markingShippedLine, setMarkingShippedLine] = useState(null);

    const [loadError, setLoadError] = useState("");
    const load = async () => {
        setLoadError("");
        try {
            const d = await withPageRetry(() => getOrderDetail(purchaseOrderId));
            setLines(d.lines);

            // Auto-select every shippable line by default - seller can still
            // uncheck individual ones before shipping/buying a label.
            const shippableLineNumbers = d.lines
                .filter((l) => l.status === "Acknowledged" && !l.intent_to_cancel && !hasUnshippedLabel(l))
                .map((l) => l.line_number);
            setSelectedLines(new Set(shippableLineNumbers));

            // Fetch wpid in the background - don't block the popup from
            // showing on this. Links just fall back to search until it lands.
            const catalogIds = [...new Set(d.lines.map((l) => l.catalog_item_id).filter(Boolean))];
            if (catalogIds.length > 0) {
                getItemsMetadata(catalogIds)
                    .then(({ items: metaByItem }) => {
                        setLines((prev) => prev?.map((l) => ({
                            ...l,
                            wpid: metaByItem[l.catalog_item_id]?.wpid || l.wpid || null,
                        })) || prev);
                    })
                    .catch(() => { /* non-fatal - links just fall back to search */ });
            }
        } catch (e) {
            setLoadError(e.message || "Failed to load order details");
        }
    };
    useEffect(() => { load(); }, [purchaseOrderId]);

    // Enrich any line missing its image - one at a time, gently staggered,
    // one attempt per row per modal session regardless of outcome.
    useEffect(() => {
        if (!lines?.length) return;
        const missing = lines.filter((l) => !l.image_url && !imageEnrichAttemptedRef.current.has(l.id));
        if (missing.length === 0) return;
        let cancelled = false;
        (async () => {
            for (const l of missing) {
                if (cancelled) return;
                imageEnrichAttemptedRef.current.add(l.id);
                const result = await enrichOrderImage(l.id).catch(() => null);
                if (cancelled || !result?.image_url) continue;
                setLines((prev) => prev?.map((row) => row.id === l.id ? { ...row, image_url: result.image_url } : row) || prev);
                await new Promise((r) => setTimeout(r, 250));
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lines?.length]);

    const isWfs = lines?.[0] && ["WFSFulfilled", "WFS"].includes(lines[0].ship_node_type);
    const unacknowledged = (lines || []).filter((l) => l.status === "Created");
    // Lines flagged intent_to_cancel are excluded from "shippable" entirely -
    // Walmart's own data shows ~72% of these end up Cancelled vs a ~3.8%
    // baseline, so shipping one is very likely wasted product and shipping
    // cost. The seller can still cancel it directly; they just can't ship it
    // from this UI while the flag is active.
    // Lines that already have a bought-but-unshipped label are excluded here
    // too - re-running the buy flow on them would purchase a SECOND label.
    // They get their own "Mark as Shipped" action below instead.
    const shippable = (lines || []).filter((l) => l.status === "Acknowledged" && !l.intent_to_cancel && !hasUnshippedLabel(l));
    const labelReadyLines = (lines || []).filter((l) => hasUnshippedLabel(l) && !l.intent_to_cancel);

    const handleAcknowledge = async () => {
        setAcking(true);
        setError("");
        try {
            await acknowledgeOrder(purchaseOrderId, unacknowledged.map((l) => l.line_number));
            await load();
            onChanged();
        } catch (e) {
            setError(e.message || "Failed to acknowledge");
        } finally {
            setAcking(false);
        }
    };

    const handleCancelLine = async (line) => {
        setCancellingLine(line.line_number);
        setError("");
        try {
            await cancelOrderLines(purchaseOrderId, [{
                line_number: line.line_number, quantity: line.quantity, reason: cancelReason,
            }]);
            setConfirmCancelLine(null);
            setSelectedLines((prev) => {
                const next = new Set(prev);
                next.delete(line.line_number);
                return next;
            });
            await load();
            onChanged();
        } catch (e) {
            setError(e.message || "Failed to cancel this line");
        } finally {
            setCancellingLine(null);
        }
    };

    const handleMarkLineShipped = async (line) => {
        setMarkingShippedLine(line.line_number);
        setError("");
        try {
            await shipOrder(purchaseOrderId, [{
                line_number: line.line_number, quantity: line.quantity,
                carrier: line.sww_label_carrier, tracking_number: line.sww_label_tracking_number,
            }]);
            await load();
            onChanged();
        } catch (e) {
            setError(e.message || "Failed to mark this line as shipped");
        } finally {
            setMarkingShippedLine(null);
        }
    };

    const handleMarkAllLabelReadyShipped = async () => {
        setMarkingShippedLine("__all__");
        setError("");
        try {
            await shipOrder(purchaseOrderId, labelReadyLines.map((l) => ({
                line_number: l.line_number, quantity: l.quantity,
                carrier: l.sww_label_carrier, tracking_number: l.sww_label_tracking_number,
            })));
            await load();
            onChanged();
        } catch (e) {
            setError(e.message || "Failed to mark these lines as shipped");
        } finally {
            setMarkingShippedLine(null);
        }
    };

    const toggleLine = (lineNumber) => setSelectedLines((prev) => {
        const next = new Set(prev);
        next.has(lineNumber) ? next.delete(lineNumber) : next.add(lineNumber);
        return next;
    });

    const handleShip = async () => {
        if (!carrier || !trackingNumber || selectedLines.size === 0) return;
        setShipping(true);
        setError("");
        try {
            const linesToShip = shippable
                .filter((l) => selectedLines.has(l.line_number))
                .map((l) => ({ line_number: l.line_number, quantity: l.quantity, carrier, tracking_number: trackingNumber }));
            await shipOrder(purchaseOrderId, linesToShip);
            setSelectedLines(new Set());
            setCarrier("");
            setTrackingNumber("");
            setShipMode(null);
            await load();
            onChanged();
        } catch (e) {
            setError(e.message || "Failed to report shipment");
        } finally {
            setShipping(false);
        }
    };

    const handleLabelBought = async () => {
        setSelectedLines(new Set());
        setShipMode(null);
        await load();
        onChanged();
    };

    if (!lines && !loadError) return null;

    if (loadError && !lines) {
        return (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 text-center" onClick={(e) => e.stopPropagation()}>
                    <p className="text-sm text-red-700 mb-3">{loadError}</p>
                    <div className="flex gap-2 justify-center">
                        <button onClick={load} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-bold hover:bg-teal-700 transition">Retry</button>
                        <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-bold text-gray-500 hover:bg-gray-50 transition">Close</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                    <div>
                        <p className="font-mono font-bold text-gray-800">{purchaseOrderId}</p>
                        {order.customer_order_id && order.customer_order_id !== purchaseOrderId && (
                            <p className="font-mono text-xs text-gray-400">Order {order.customer_order_id}</p>
                        )}
                        <p className="text-xs text-gray-400">{isWfs ? "Fulfilled by Walmart (read-only)" : "Seller-Fulfilled"}</p>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600"><X size={18} /></button>
                </div>

                <div className="p-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-xs text-red-700 flex items-start gap-2">
                            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> {error}
                        </div>
                    )}

                    <div className="space-y-2 mb-5">
                        {lines.map((l) => {
                            const isCancellable = !isWfs && ["Created", "Acknowledged"].includes(l.status);
                            const cancelRisk = l.intent_to_cancel && ["Created", "Acknowledged"].includes(l.status);
                            return (
                                <div key={l.line_number}
                                    className={`border rounded-xl p-3 transition ${cancelRisk ? "border-red-300 bg-red-50/50"
                                        : selectedLines.has(l.line_number) ? "border-teal-300 bg-teal-50/50" : "border-gray-100"}`}>
                                    <div className="flex items-center gap-3">
                                        {!isWfs && !cancelRisk && (
                                            <div className="flex-shrink-0">
                                                {l.status === "Acknowledged" && !hasUnshippedLabel(l) ? (
                                                    <input type="checkbox" checked={selectedLines.has(l.line_number)}
                                                        onChange={() => toggleLine(l.line_number)} className="w-4 h-4 accent-teal-600" />
                                                ) : (
                                                    <div className="w-4 h-4" />
                                                )}
                                            </div>
                                        )}
                                        <a href={walmartItemUrl(l)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                            <ProductImage src={l.image_url} />
                                        </a>
                                        <div className="flex-1 min-w-0">
                                            <a href={walmartItemUrl(l)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                                                className="text-sm font-semibold text-gray-800 truncate hover:text-teal-600 hover:underline block" title={l.product_name}>
                                                {l.product_name}
                                            </a>
                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-400 mt-0.5">
                                                <span className="font-mono">{l.sku}</span>
                                                <span>Qty {l.quantity}</span>
                                                <span className="font-semibold text-gray-600">{money(l.unit_price)}</span>
                                                <LoadIncentiveButton sku={l.sku} soldPrice={l.unit_price} />
                                            </div>
                                            {cancelRisk && (
                                                <p className="text-[11px] text-red-600 font-bold mt-1 flex items-center gap-1">
                                                    🔴 Customer requested cancellation - do not ship
                                                </p>
                                            )}
                                        </div>
                                        {!isWfs && hasUnshippedLabel(l) && (
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <PackingSlipButton order={order} lines={[l]} />
                                                <DownloadSwwLabelButton carrier={l.sww_label_carrier} trackingNumber={l.sww_label_tracking_number} />
                                                <button onClick={() => handleMarkLineShipped(l)} disabled={markingShippedLine === l.line_number || markingShippedLine === "__all__"}
                                                    className="text-[10px] font-bold px-2 py-1 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition disabled:opacity-50 whitespace-nowrap">
                                                    {markingShippedLine === l.line_number ? "Marking..." : "Mark Shipped Now"}
                                                </button>
                                            </div>
                                        )}
                                        <StatusBadge status={lineDisplayStatus(l)} />
                                        {isCancellable && confirmCancelLine !== l.line_number && (
                                            <button onClick={() => { setCancelReason("SELLER_CANCEL_OUT_OF_STOCK"); setConfirmCancelLine(l.line_number); }}
                                                className="text-[11px] font-bold text-red-500 hover:text-red-600 flex-shrink-0">
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                    {confirmCancelLine === l.line_number && (
                                        <div className="mt-2.5 pt-2.5 border-t border-red-100">
                                            <div className="flex items-center justify-between gap-2 mb-2">
                                                <p className="text-[11px] text-red-600 flex-1">
                                                    Cancel this line on Walmart? This can't be undone.
                                                </p>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <button onClick={() => setConfirmCancelLine(null)} disabled={cancellingLine === l.line_number}
                                                        className="text-[11px] font-bold text-gray-400 hover:text-gray-600 disabled:opacity-50">
                                                        Never mind
                                                    </button>
                                                    <button onClick={() => handleCancelLine(l)} disabled={cancellingLine === l.line_number}
                                                        className="text-[11px] font-bold px-2.5 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition disabled:opacity-50">
                                                        {cancellingLine === l.line_number ? "Cancelling..." : "Yes, Cancel It"}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <label className="text-[11px] font-bold text-gray-500 whitespace-nowrap">Reason:</label>
                                                <select value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                                                    disabled={cancellingLine === l.line_number}
                                                    className="flex-1 border border-gray-200 rounded-lg px-2 py-1 text-[11px] focus:outline-none focus:border-red-400 bg-white">
                                                    {CANCEL_REASON_OPTIONS.map((r) => (
                                                        <option key={r.value} value={r.value}>{r.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {!isWfs && unacknowledged.length > 0 && (
                        <button onClick={handleAcknowledge} disabled={acking}
                            className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition disabled:opacity-50 mb-3">
                            {acking ? "Acknowledging..." : `Acknowledge ${unacknowledged.length} Line(s)`}
                        </button>
                    )}

                    {!isWfs && labelReadyLines.length > 0 && (
                        <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 mb-3 flex items-center justify-between gap-2 flex-wrap">
                            <p className="text-xs text-teal-700">
                                <span className="font-bold">{labelReadyLines.length} line(s)</span> have a label bought but haven't been marked shipped yet.
                            </p>
                            <button onClick={handleMarkAllLabelReadyShipped} disabled={markingShippedLine !== null}
                                className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-bold hover:bg-teal-700 transition disabled:opacity-50">
                                {markingShippedLine === "__all__" ? "Marking..." : `Mark All ${labelReadyLines.length} Shipped`}
                            </button>
                        </div>
                    )}

                    {!isWfs && shippable.length > 0 && (
                        <div>
                            <p className="text-xs font-bold text-gray-600 mb-2">Ship Selected Lines ({selectedLines.size} selected)</p>

                            {!shipMode && (
                                <div className="grid grid-cols-2 gap-3">
                                    <button onClick={() => setShipMode("walmart")} disabled={selectedLines.size === 0}
                                        className="p-3 border-2 border-gray-200 hover:border-teal-400 rounded-xl text-left text-xs transition disabled:opacity-40 disabled:hover:border-gray-200">
                                        <p className="font-bold text-gray-700 mb-0.5 flex items-center gap-1.5"><CreditCard size={14} className="text-blue-600" /> Buy a Label via Walmart</p>
                                        <p className="text-gray-400">Get rates and purchase postage through Walmart</p>
                                    </button>
                                    <button onClick={() => setShipMode("self")} disabled={selectedLines.size === 0}
                                        className="p-3 border-2 border-gray-200 hover:border-teal-400 rounded-xl text-left text-xs transition disabled:opacity-40 disabled:hover:border-gray-200">
                                        <p className="font-bold text-gray-700 mb-0.5 flex items-center gap-1.5"><Truck size={14} className="text-teal-600" /> I'm Shipping This Myself</p>
                                        <p className="text-gray-400">Enter your own carrier and tracking number</p>
                                    </button>
                                </div>
                            )}
                            {!shipMode && selectedLines.size === 0 && (
                                <p className="text-[11px] text-gray-400 mt-1.5">Check one or more Acknowledged lines above to enable shipping.</p>
                            )}

                            {shipMode === "self" && (
                                <div className="border border-gray-200 rounded-xl p-4">
                                    <button onClick={() => setShipMode(null)} className="text-[11px] text-gray-400 hover:text-gray-600 mb-2">← Choose a different option</button>
                                    <div className="grid grid-cols-2 gap-2 mb-2">
                                        <input placeholder="Carrier (e.g. UPS, FedEx)" value={carrier} onChange={(e) => setCarrier(e.target.value)}
                                            className="border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-teal-500" />
                                        <input placeholder="Tracking number" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)}
                                            className="border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-teal-500" />
                                    </div>
                                    <button onClick={handleShip} disabled={shipping || selectedLines.size === 0 || !carrier || !trackingNumber}
                                        className="w-full py-2.5 bg-teal-600 text-white rounded-lg text-sm font-bold hover:bg-teal-700 transition disabled:opacity-50 flex items-center justify-center gap-1.5">
                                        {shipping ? "Reporting shipment..." : <><Truck size={14} /> Mark as Shipped</>}
                                    </button>
                                </div>
                            )}

                            {shipMode === "walmart" && (
                                <div>
                                    <button onClick={() => setShipMode(null)} className="text-[11px] text-gray-400 hover:text-gray-600 mb-2">← Choose a different option</button>
                                    <BuyLabelPanel order={order} lines={lines} shippableLines={shippable} selectedLines={selectedLines} onBought={handleLabelBought} />
                                </div>
                            )}
                        </div>
                    )}

                    {isWfs && (
                        <p className="text-xs text-gray-400 text-center py-4">
                            Walmart handles acknowledgement, packing, and shipping for WFS orders - nothing to do here.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

const PICK_LIST_FETCH_SIZE = 500;

// Aggregates every Acknowledged-but-not-shipped SF line, across ALL pages
// (not just the current 25), into one row per SKU with the total quantity
// still owed to the warehouse. Loops listOrders the same way
// handleBulkAcknowledge's "across pages" path already does, just for
// reading instead of acknowledging.
const PickListModal = ({ onClose }) => {
    const [rows, setRows] = useState(null);
    const [loadError, setLoadError] = useState("");
    const [picking, setPicking] = useState(false);
    const [pickedCounts, setPickedCounts] = useState({});
    const [scanValue, setScanValue] = useState("");
    const [scanError, setScanError] = useState("");
    const [lastScannedSku, setLastScannedSku] = useState(null);
    const [showOnlyRemaining, setShowOnlyRemaining] = useState(false);
    const scanInputRef = useRef(null);

    const load = async () => {
        setLoadError("");
        setRows(null);
        try {
            // Fetch Created and Acknowledged separately, server-side filtered -
            // each is a cheap, bounded query (matches the tiny subset of orders
            // still in progress), instead of paging through the seller's ENTIRE
            // SF order history (which "no status filter" would require, since
            // fulfillment=sf paginates by PO group and d.total then means
            // "every SF order ever", not just unshipped ones).
            const fetchAllForStatus = async (status) => {
                let page = 1;
                let acc = [];
                // eslint-disable-next-line no-constant-condition
                while (true) {
                    const d = await listOrders("sf", status, undefined, page, PICK_LIST_FETCH_SIZE);
                    acc = acc.concat(d.orders || []);
                    if (acc.length >= (d.total || 0) || (d.orders || []).length === 0) break;
                    page += 1;
                }
                return acc;
            };
            const [createdRows, acknowledgedRows] = await Promise.all([
                fetchAllForStatus("Created"),
                fetchAllForStatus("Acknowledged"),
            ]);
            const deduped = dedupeByLineNumber([...createdRows, ...acknowledgedRows]);

            const bySku = new Map();
            for (const l of deduped) {
                if (l.intent_to_cancel) continue; // very likely to be cancelled - don't send anyone to go grab it
                const key = l.sku || l.line_number;
                if (!bySku.has(key)) {
                    bySku.set(key, {
                        sku: l.sku, product_name: l.product_name, image_url: l.image_url,
                        catalog_item_id: l.catalog_item_id, upc: "",
                        totalQty: 0,
                    });
                }
                const entry = bySku.get(key);
                entry.totalQty += l.quantity || 0;
                if (!entry.image_url && l.image_url) entry.image_url = l.image_url;
            }

            // Order lines only carry catalog_item_id, not gtin/upc directly -
            // resolve those in one batched call against the catalog.
            const catalogIds = [...new Set([...bySku.values()].map((r) => r.catalog_item_id).filter(Boolean))];
            if (catalogIds.length > 0) {
                try {
                    const { items: metaByItem } = await getItemsMetadata(catalogIds);
                    for (const entry of bySku.values()) {
                        entry.upc = metaByItem[entry.catalog_item_id]?.gtin || metaByItem[entry.catalog_item_id]?.upc || "";
                    }
                } catch { /* non-fatal - pick list still works without UPC/GTIN */ }
            }

            setRows(Array.from(bySku.values()).sort((a, b) => (a.product_name || "").localeCompare(b.product_name || "")));
        } catch (e) {
            setLoadError(e.message || "Failed to build pick list");
        }
    };

    useEffect(() => { load(); }, []);
    useEffect(() => { if (picking) scanInputRef.current?.focus(); }, [picking]);

    const totalItems = rows?.length || 0;
    const pickedItems = rows?.filter((r) => (pickedCounts[r.sku] || 0) >= r.totalQty).length || 0;
    const allPicked = totalItems > 0 && pickedItems === totalItems;

    const sortedRows = useMemo(() => {
        if (!rows) return [];
        const rank = (r) => {
            if (r.sku === lastScannedSku) return 0;
            return (pickedCounts[r.sku] || 0) >= r.totalQty ? 2 : 1;
        };
        return [...rows].sort((a, b) => rank(a) - rank(b));
    }, [rows, pickedCounts, lastScannedSku]);

    const displayRows = (picking && showOnlyRemaining)
        ? sortedRows.filter((r) => (pickedCounts[r.sku] || 0) < r.totalQty)
        : sortedRows;

    const togglePicked = (sku, totalQty) => setPickedCounts((prev) => {
        const current = prev[sku] || 0;
        return { ...prev, [sku]: current >= totalQty ? 0 : totalQty };
    });

    const processScan = (raw) => {
        const code = normalizeScanCode(raw);
        setScanValue("");
        if (!code) return;
        const match = rows.find((r) => scanCodesMatch(normalizeScanCode(r.upc), code) || scanCodesMatch(normalizeScanCode(r.sku), code));
        if (!match) {
            setScanError(`No item in this pick list matches "${code}".`);
            playPickErrorBeep();
            return;
        }
        const current = pickedCounts[match.sku] || 0;
        if (current >= match.totalQty) {
            setScanError(`${match.product_name || match.sku} is already fully picked (${match.totalQty}/${match.totalQty}).`);
            playPickErrorBeep();
            return;
        }
        setPickedCounts((prev) => ({ ...prev, [match.sku]: current + 1 }));
        setLastScannedSku(match.sku);
        setScanError("");
        playPickSuccessDing();
    };

    const handleScanChange = (e) => {
        const raw = e.target.value;
        const digits = normalizeScanCode(raw);
        if (digits.length >= 8 && digits.length <= 14) processScan(raw);
        else setScanValue(raw);
    };

    const printPickListPDF = () => {
        const win = window.open("", "_blank");
        const html = `
        <!DOCTYPE html><html><head><title>Pick List</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #1f2937; }
          h1 { color: #0071ce; margin-bottom: 4px; }
          table { width: 100%; border-collapse: collapse; margin-top: 16px; }
          th { background: #0071ce; color: white; padding: 8px 12px; text-align: left; font-size: 13px; }
          td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 12px; vertical-align: middle; }
          tr:nth-child(even) { background: #f9fafb; }
          img { width: 50px; height: 50px; object-fit: contain; }
          .no-img { width: 40px; height: 40px; background: #f3f4f6; display: inline-block; border-radius: 4px; }
          .qty { font-weight: bold; font-size: 20px; color: #0071ce; }
          .meta { color: #6b7280; font-size: 12px; margin-bottom: 8px; }
        </style></head><body>
        <h1>Pick List</h1>
        <p class="meta">Generated: ${new Date().toLocaleString()} &nbsp;|&nbsp; ${rows.length} item(s)</p>
        <table>
          <thead><tr><th style="width:60px;">Image</th><th>Item Name</th><th style="width:130px;">SKU</th><th style="width:130px;">UPC/GTIN</th><th style="width:70px;">Qty to Pick</th></tr></thead>
          <tbody>
            ${rows.map((r) => `<tr>
                <td>${r.image_url ? `<img src="${r.image_url}" />` : '<span class="no-img"></span>'}</td>
                <td>${r.product_name || ""}</td>
                <td style="font-family:monospace;">${r.sku || "—"}</td>
                <td style="font-family:monospace;">${r.upc || "—"}</td>
                <td class="qty">${r.totalQty}</td>
              </tr>`).join("")}
          </tbody>
        </table>
        </body></html>`;
        win.document.write(html);
        win.document.close();
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h2 className="font-bold text-gray-800 text-lg flex items-center gap-2"><ClipboardList size={20} className="text-purple-600" /> Pick List</h2>
                        <p className="text-xs text-gray-400 mt-0.5">Everything Acknowledged but not yet shipped - grab these from the warehouse.</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
                </div>

                <div className="px-6 py-3 flex items-center gap-2 flex-shrink-0 bg-gray-50 border-b border-gray-100 flex-wrap">
                    <button onClick={printPickListPDF} disabled={!rows || rows.length === 0}
                        className="px-4 py-2 rounded-lg text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 transition disabled:opacity-40 flex items-center gap-1.5">
                        <Printer size={13} /> Print / Save PDF
                    </button>
                    <button
                        onClick={() => setPicking((p) => { if (p) setShowOnlyRemaining(false); return !p; })}
                        disabled={!rows || rows.length === 0}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border transition disabled:opacity-40 ${picking ? "bg-gray-100 text-gray-600 border-gray-200" : "bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100"}`}>
                        {picking ? "Stop Picking" : "Start Picking"}
                    </button>
                    {picking && totalItems > 0 && (
                        <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${allPicked ? "bg-green-50 text-green-600" : "bg-orange-50 text-orange-600"}`}>
                            {pickedItems}/{totalItems} items picked
                        </span>
                    )}
                    <button onClick={load} className="ml-auto text-xs text-gray-400 hover:text-gray-600 font-semibold">Refresh</button>
                </div>

                {picking && (
                    <div className="px-6 py-3 flex-shrink-0 border-b border-gray-100">
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
                            <input
                                ref={scanInputRef}
                                value={scanValue}
                                onChange={handleScanChange}
                                onKeyDown={(e) => { if (e.key === "Enter") processScan(scanValue); }}
                                placeholder="Scan or paste UPC/GTIN/SKU..."
                                className={`w-full border rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none ${scanError ? "border-red-300 bg-red-50" : "border-gray-200 focus:border-teal-500"}`}
                            />
                        </div>
                        {scanError && <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1"><AlertCircle size={12} /> {scanError}</p>}
                        <label className="flex items-center gap-1.5 text-xs text-gray-500 mt-2 cursor-pointer w-fit">
                            <input type="checkbox" checked={showOnlyRemaining} onChange={(e) => setShowOnlyRemaining(e.target.checked)} className="accent-teal-600" />
                            Only show what's left to pick
                        </label>
                    </div>
                )}

                <div className="overflow-y-auto flex-1 px-6 py-4">
                    {loadError && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700">{loadError}</div>}
                    {!loadError && rows === null && (
                        <div className="flex items-center gap-2 text-gray-400 text-sm py-10 justify-center"><Loader size={16} className="animate-spin" /> Building pick list...</div>
                    )}
                    {rows?.length === 0 && <p className="text-gray-400 text-sm py-10 text-center">Nothing to pick - all caught up!</p>}
                    {rows?.length > 0 && (
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-gray-400 text-left border-b border-gray-100">
                                    {picking && <th className="py-2 w-8"></th>}
                                    <th className="py-2 w-14">Image</th>
                                    <th className="py-2">Item</th>
                                    <th className="py-2 w-28">SKU</th>
                                    <th className="py-2 w-28">UPC/GTIN</th>
                                    <th className="py-2 w-20 text-right">Qty</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayRows.map((r) => {
                                    const count = pickedCounts[r.sku] || 0;
                                    const done = count >= r.totalQty;
                                    return (
                                        <tr key={r.sku} className={`border-b border-gray-50 ${done ? "opacity-40" : ""} ${r.sku === lastScannedSku ? "bg-teal-50/60" : ""}`}>
                                            {picking && (
                                                <td className="py-2">
                                                    <input type="checkbox" checked={done} onChange={() => togglePicked(r.sku, r.totalQty)} className="w-4 h-4 accent-teal-600" />
                                                </td>
                                            )}
                                            <td className="py-2"><ProductImage src={r.image_url} /></td>
                                            <td className={`py-2 ${done ? "line-through" : ""}`} title={r.product_name}>{truncateTitle(r.product_name, 50)}</td>
                                            <td className={`py-2 font-mono text-gray-500 ${done ? "line-through" : ""}`}>{r.sku || "—"}</td>
                                            <td className={`py-2 font-mono text-gray-500 ${done ? "line-through" : ""}`}>{r.upc || "—"}</td>
                                            <td className={`py-2 text-right ${done ? "line-through" : ""}`}>
                                                {picking ? (
                                                    <span className={`font-bold text-lg ${done ? "text-green-600" : "text-orange-600"}`}>{count}/{r.totalQty}</span>
                                                ) : (
                                                    <span className="font-bold text-xl text-gray-800">{r.totalQty}</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

const OrdersPage = () => {
    const [tab, setTab] = useState(() => {
        try {
            const saved = localStorage.getItem(LAST_ORDERS_TAB_KEY);
            return saved === "wfs" || saved === "sf" ? saved : "sf";
        } catch {
            return "sf";
        }
    });
    const [orders, setOrders] = useState(null);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("default"); // default | order_date_desc | order_date_asc | ship_by_asc | ship_by_desc
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [pickListOpen, setPickListOpen] = useState(false);
    const [loadError, setLoadError] = useState("");

    // Bulk acknowledge - SF tab only. Keyed by purchase_order_id, not
    // individual line, since acknowledging is an order-level action here
    // (matches Seller Center's own "select orders, acknowledge" flow).
    const [selectedPOs, setSelectedPOs] = useState(new Set());
    const [bulkAcking, setBulkAcking] = useState(false);
    const [bulkAckError, setBulkAckError] = useState("");
    // True total across every matching order (not just this page) -
    // comes from the backend's group-based count. `selectAllAcrossPages`
    // tracks whether "select all" means "everything on this page" (old
    // behavior) or "genuinely every matching order" (new).
    const [totalNeedsAck, setTotalNeedsAck] = useState(0);
    const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false);

    // Tags every load() call so a slow/late response from a PREVIOUS tab or
    // filter (e.g. switching sf -> wfs fires a request, then flipping back
    // to sf fires another before the first ever resolves) can never land
    // after a newer one and overwrite/mix into the current list.
    const loadRequestIdRef = useRef(0);

    const load = async () => {
        const requestId = ++loadRequestIdRef.current;
        setLoading(true);
        setLoadError("");
        try {
            const d = await withPageRetry(() =>
                listOrders(tab, statusFilter || undefined, search || undefined, page, PAGE_SIZE)
            );
            if (requestId !== loadRequestIdRef.current) return; // a newer request already won
            setOrders(d.orders || []);
            setTotal(d.total || 0);
            setTotalNeedsAck(d.total_needs_ack || 0);
        } catch (e) {
            if (requestId !== loadRequestIdRef.current) return;
            setLoadError(e.message || "Failed to load orders");
        } finally {
            if (requestId === loadRequestIdRef.current) setLoading(false);
        }
    };
    useEffect(() => { load(); }, [tab, statusFilter, page]);

    useEffect(() => {
        try { localStorage.setItem(LAST_ORDERS_TAB_KEY, tab); } catch { /* non-fatal */ }
    }, [tab]);

    // Reset immediately on tab/filter change - don't wait for the new
    // page's response to land before clearing the OLD tab's list off screen.
    useEffect(() => {
        setOrders(null);
        setTotal(0);
        setPage(1);
        setSelectedPOs(new Set());
        setSelectAllAcrossPages(false);
        setTotalNeedsAck(0);
        setBulkAckError("");
    }, [tab, statusFilter]);

    // Walmart returns one row per LINE ITEM, not per order, and some rows
    // are exact duplicates (same purchase_order_id + line_number) from a
    // backend sync artifact. Declared here, BEFORE any effect that reads
    // it, since a dependency array is evaluated synchronously at render
    // time - reading a useMemo declared later in the file throws
    // "Cannot access before initialization", same as a plain const would.
    const dedupedOrders = useMemo(() => (orders ? dedupeByLineNumber(orders) : null), [orders]);

    // Ref (not state) so it persists across reloads/filter changes within
    // this page session - prevents an unfixable row (no SKU match, no
    // GTIN/UPC/wpid at all) from being re-attempted on every single load.
    const imageEnrichAttemptedRef = useRef(new Set());

    useEffect(() => {
        if (!dedupedOrders) return;
        const missing = dedupedOrders.filter((o) => !o.image_url && !imageEnrichAttemptedRef.current.has(o.id));
        if (missing.length === 0) return;
        let cancelled = false;
        (async () => {
            for (const o of missing) {
                if (cancelled) return;
                imageEnrichAttemptedRef.current.add(o.id); // marked before the call - one attempt, regardless of outcome
                const result = await enrichOrderImage(o.id).catch(() => null);
                if (cancelled || !result?.image_url) continue;
                setOrders((prev) => prev?.map((row) => row.id === o.id ? { ...row, image_url: result.image_url } : row) || prev);
                await new Promise((r) => setTimeout(r, 250));
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dedupedOrders?.map((o) => o.id).join(",")]);

    // Group same purchase_order_id together so multi-item orders show as
    // ONE card (same customer, same address, same shipment), matching how
    // Seller Central itself presents orders. Clicking the card still opens
    // OrderDetailModal, which already fetches every line for this PO -
    // nothing there needs to change.
    const orderGroups = useMemo(() => {
        if (!dedupedOrders) return null;
        const map = new Map();
        for (const o of dedupedOrders) {
            if (!map.has(o.purchase_order_id)) map.set(o.purchase_order_id, []);
            map.get(o.purchase_order_id).push(o);
        }
        return Array.from(map.values());
    }, [dedupedOrders]);

    // Sorting only reorders what's already loaded on the CURRENT page - it
    // doesn't re-query the backend, so results won't be sorted across pages.
    const sortComparator = (aDate, bDate, direction) => {
        const aTime = aDate ? new Date(aDate).getTime() : null;
        const bTime = bDate ? new Date(bDate).getTime() : null;
        if (aTime === null && bTime === null) return 0;
        if (aTime === null) return 1;  // push missing dates to the end
        if (bTime === null) return -1;
        return direction === "asc" ? aTime - bTime : bTime - aTime;
    };

    const applySortBy = (aDate, bDate) => {
        if (sortBy === "order_date_asc" || sortBy === "order_date_desc") return null; // handled by caller with order_date
        return null;
    };

    const sortedOrderGroups = useMemo(() => {
        if (!orderGroups) return null;
        if (sortBy === "default") return orderGroups; // exactly as the backend returned it
        const sorted = [...orderGroups];
        sorted.sort((groupA, groupB) => {
            const a = groupA[0], b = groupB[0];
            if (sortBy === "order_date_asc") return sortComparator(a.order_date, b.order_date, "asc");
            if (sortBy === "order_date_desc") return sortComparator(a.order_date, b.order_date, "desc");
            if (sortBy === "ship_by_asc") return sortComparator(a.ship_by_date, b.ship_by_date, "asc");
            if (sortBy === "ship_by_desc") return sortComparator(a.ship_by_date, b.ship_by_date, "desc");
            return 0;
        });
        return sorted;
    }, [orderGroups, sortBy]);

    const sortedDedupedOrders = useMemo(() => {
        if (!dedupedOrders) return null;
        if (sortBy === "default") return dedupedOrders; // exactly as the backend returned it
        const sorted = [...dedupedOrders];
        sorted.sort((a, b) => {
            if (sortBy === "order_date_asc") return sortComparator(a.order_date, b.order_date, "asc");
            if (sortBy === "order_date_desc") return sortComparator(a.order_date, b.order_date, "desc");
            if (sortBy === "ship_by_asc") return sortComparator(a.ship_by_date, b.ship_by_date, "asc");
            if (sortBy === "ship_by_desc") return sortComparator(a.ship_by_date, b.ship_by_date, "desc");
            return 0;
        });
        return sorted;
    }, [dedupedOrders, sortBy]);

    // An order group is eligible for bulk acknowledge if it has at least
    // one line still in "Created" status - mixed-status groups (some lines
    // already acknowledged) are still eligible, since acknowledging only
    // ever touches the still-Created lines within it.
    const acknowledgeableGroups = useMemo(() => {
        if (tab !== "sf" || !orderGroups) return [];
        return orderGroups.filter((group) => group.some((l) => l.status === "Created"));
    }, [tab, orderGroups]);

    // Any manual per-row toggle drops out of "select all across pages" -
    // the seller is now making a deliberate page-scoped choice instead.
    const toggleSelectPO = (poId) => {
        setSelectAllAcrossPages(false);
        setSelectedPOs((prev) => {
            const next = new Set(prev);
            next.has(poId) ? next.delete(poId) : next.add(poId);
            return next;
        });
    };

    const toggleSelectAllAcknowledgeable = () => {
        if (selectAllAcrossPages) {
            setSelectAllAcrossPages(false);
            setSelectedPOs(new Set());
        } else {
            setSelectAllAcrossPages(true);
            // Also mark the current page's boxes as checked visually -
            // the actual bulk action below uses selectAllAcrossPages, not
            // this set, once that flag is true.
            setSelectedPOs(new Set(acknowledgeableGroups.map((g) => g[0].purchase_order_id)));
        }
    };

    const handleBulkAcknowledge = async () => {
        setBulkAcking(true);
        setBulkAckError("");
        try {
            if (selectAllAcrossPages) {
                // One backend call handles every matching order across
                // every page - not just what's currently loaded.
                const result = await acknowledgeAllOrders(statusFilter || undefined, search || undefined);
                if (result.failed?.length > 0) {
                    setBulkAckError(`${result.failed.length} order(s) failed to acknowledge: ${result.failed.join(", ")}`);
                }
            } else {
                // Sequential, not Promise.all - this hits Walmart's real API
                // once per order, and running them one at a time avoids
                // slamming Walmart with a burst of simultaneous acknowledge
                // calls if the seller selects a large page-scoped batch.
                const failures = [];
                for (const poId of selectedPOs) {
                    const group = acknowledgeableGroups.find((g) => g[0].purchase_order_id === poId);
                    if (!group) continue;
                    const lineNumbers = group.filter((l) => l.status === "Created").map((l) => l.line_number);
                    if (lineNumbers.length === 0) continue;
                    try {
                        await acknowledgeOrder(poId, lineNumbers);
                    } catch (e) {
                        failures.push(poId);
                    }
                }
                if (failures.length > 0) {
                    setBulkAckError(`${failures.length} order(s) failed to acknowledge: ${failures.join(", ")}`);
                }
            }
            setSelectedPOs(new Set());
            setSelectAllAcrossPages(false);
            await load();
        } finally {
            setBulkAcking(false);
        }
    };

    // Flags when the SAME buyer (name + zip) has more than one DISTINCT
    // purchase_order_id on the current page - e.g. ordered twice separately
    // rather than one order with multiple lines. Purely a visual hint (a
    // star + tooltip on the buyer name); it never merges, selects, or ships
    // anything, so two real Walmart orders can never accidentally get
    // treated as one.
    const buyerOrderCounts = useMemo(() => {
        if (!dedupedOrders) return new Map();
        const poToBuyer = new Map();
        for (const o of dedupedOrders) {
            if (!poToBuyer.has(o.purchase_order_id)) {
                poToBuyer.set(o.purchase_order_id, { buyer_name: o.buyer_name, postal_code: o.postal_code });
            }
        }
        const counts = new Map();
        for (const { buyer_name, postal_code } of poToBuyer.values()) {
            if (!buyer_name) continue;
            const key = `${buyer_name}|${postal_code || ""}`;
            counts.set(key, (counts.get(key) || 0) + 1);
        }
        return counts;
    }, [dedupedOrders]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="max-w-5xl mx-auto py-8 px-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-1">Orders</h1>
            <p className="text-gray-500 text-sm mb-6">Seller-Fulfilled orders need action here. WFS orders are handled entirely by Walmart.</p>

            <div className="flex gap-1 mb-5 border-b border-gray-200">
                <button onClick={() => setTab("sf")}
                    className={`px-4 py-2.5 text-sm font-bold border-b-2 transition flex items-center gap-1.5 ${tab === "sf" ? "border-teal-500 text-teal-700" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
                    <Truck size={14} /> Seller-Fulfilled
                </button>
                <button onClick={() => setTab("wfs")}
                    className={`px-4 py-2.5 text-sm font-bold border-b-2 transition flex items-center gap-1.5 ${tab === "wfs" ? "border-teal-500 text-teal-700" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
                    <Package size={14} /> WFS (Walmart-Fulfilled)
                </button>
            </div>

            {tab === "sf" && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 flex items-center justify-between flex-wrap gap-2">
                    {totalNeedsAck > 0 ? (
                        <label className="flex items-center gap-2 text-xs font-semibold text-blue-700 cursor-pointer">
                            <input type="checkbox"
                                checked={selectAllAcrossPages || (selectedPOs.size === acknowledgeableGroups.length && acknowledgeableGroups.length > 0)}
                                onChange={toggleSelectAllAcknowledgeable}
                                className="w-4 h-4 accent-blue-600" />
                            Select all needing acknowledgement ({totalNeedsAck}
                            {totalNeedsAck !== acknowledgeableGroups.length ? ` total - ${acknowledgeableGroups.length} on this page` : ""})
                        </label>
                    ) : <span />}
                    <div className="flex items-center gap-2">
                        <button onClick={() => setPickListOpen(true)}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-xs font-bold hover:bg-purple-700 transition flex items-center gap-1.5">
                            <ClipboardList size={14} /> Pick List
                        </button>
                        {totalNeedsAck > 0 && (
                            <button onClick={handleBulkAcknowledge} disabled={(selectedPOs.size === 0 && !selectAllAcrossPages) || bulkAcking}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition disabled:opacity-40">
                                {bulkAcking
                                    ? "Acknowledging..."
                                    : selectAllAcrossPages
                                        ? `Acknowledge All (${totalNeedsAck})`
                                        : `Acknowledge Selected (${selectedPOs.size})`}
                            </button>
                        )}
                    </div>
                </div>
            )}
            {bulkAckError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700">{bulkAckError}</div>
            )}

            <div className="bg-white rounded-xl shadow p-3 mb-4 flex flex-wrap items-center gap-2">
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
                    placeholder="Search by PO#, SKU, or product name..."
                    className="flex-1 min-w-[200px] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500">
                    <option value="">All statuses</option>
                    <option value="Created">Created (needs acknowledgement)</option>
                    <option value="Acknowledged">Acknowledged (needs shipping)</option>
                    <option value="Shipped">Shipped</option>
                    <option value="Delivered">Delivered</option>
                    <option value="Cancelled">Cancelled</option>
                </select>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500">
                    <option value="default">Default order</option>
                    <option value="order_date_desc">Order date (newest first)</option>
                    <option value="order_date_asc">Order date (oldest first)</option>
                    <option value="ship_by_asc">Ship by date (soonest first)</option>
                    <option value="ship_by_desc">Ship by date (latest first)</option>
                </select>
                <button onClick={() => { setPage(1); load(); }} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-bold hover:bg-teal-700 transition flex items-center gap-1.5">
                    <Search size={14} /> Search
                </button>
                <LoadAllIncentivesButton />
            </div>

            {orders === null && !loadError && (
                <div className="space-y-2">
                    {Array.from({ length: 6 }).map((_, i) => <OrderCardSkeleton key={i} />)}
                </div>
            )}
            {loadError && orders === null && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                    <p className="text-sm text-red-700 mb-2">{loadError}</p>
                    <button onClick={load} className="px-4 py-1.5 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700 transition">
                        Retry
                    </button>
                </div>
            )}
            {orders?.length === 0 && !loading && <p className="text-gray-400 text-sm py-10 text-center">No orders found.</p>}

            <div key={tab} className={`space-y-2 transition-opacity ${loading && orders !== null ? "opacity-50 pointer-events-none" : ""}`}>
                {tab === "wfs" ? (
                    // WFS is read-only and Walmart-fulfilled - left exactly as
                    // one card per line, ungrouped, per your call. Only the
                    // buyer-duplicate star is added, since that's informational
                    // only and changes nothing else here. dedupedOrders removes
                    // exact-duplicate (same PO + line_number) rows so this list
                    // always matches what the detail popup shows.
                    sortedDedupedOrders?.map((o) => {
                        const isOverdue = o.ship_by_date && !["Shipped", "Delivered", "Cancelled"].includes(o.status)
                            && new Date(o.ship_by_date) < new Date();
                        const buyerKey = o.buyer_name ? `${o.buyer_name}|${o.postal_code || ""}` : null;
                        const buyerDupeCount = buyerKey ? (buyerOrderCounts.get(buyerKey) || 0) : 0;
                        return (
                            <button key={o.id} onClick={() => setSelectedOrder(o)}
                                className="w-full bg-white rounded-xl shadow p-4 text-left hover:bg-teal-50 transition flex items-center gap-3">
                                <ProductImage src={o.image_url} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className="font-mono text-xs font-bold text-gray-500">{o.purchase_order_id}</p>
                                        {o.customer_order_id && o.customer_order_id !== o.purchase_order_id && (
                                            <span className="text-[10px] text-gray-300">· Order {o.customer_order_id}</span>
                                        )}
                                    </div>
                                    <p className="text-sm font-semibold text-gray-800 truncate" title={o.product_name}>
                                        {truncateTitle(o.product_name)}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-400 mt-0.5">
                                        <span className="font-mono">{o.sku}</span>
                                        <span>Qty {o.quantity}{o.shipped_quantity ? ` (${o.shipped_quantity} shipped)` : ""}</span>
                                        <span className="font-semibold text-gray-600">{money(o.charge_total)}</span>
                                        {o.state && <span>{o.state} {o.postal_code}</span>}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-400 mt-0.5">
                                        <span>{o.order_date ? new Date(o.order_date).toLocaleDateString() : "-"}</span>
                                        {o.buyer_name && (
                                            <span className={buyerDupeCount > 1 ? "flex items-center gap-1" : ""}
                                                title={buyerDupeCount > 1 ? `${buyerDupeCount} separate orders from this buyer at this ZIP` : undefined}>
                                                {buyerDupeCount > 1 && <p className="text-amber-600 font-semibold">({buyerDupeCount} separate orders) </p>}
                                                Buyer: {o.buyer_name}
                                            </span>
                                        )}
                                        {o.tracking_number && <span className="font-mono">Tracking: {o.tracking_number}{o.carrier ? ` (${o.carrier})` : ""}</span>}
                                        <LoadIncentiveButton sku={o.sku} soldPrice={o.unit_price ?? (o.quantity ? (o.charge_total || 0) / o.quantity : null)} />
                                        {o.ship_by_date && !["Shipped", "Delivered", "Cancelled"].includes(o.status) && (
                                            <span className={`font-semibold ${isOverdue ? "text-red-500" : "text-orange-500"}`}>
                                                {isOverdue ? "Overdue - " : "Ship by "}{new Date(o.ship_by_date).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <StatusBadge status={o.status} />
                                    <ChevronRight size={16} className="text-gray-300" />
                                </div>
                            </button>
                        );
                    })
                ) : (
                    sortedOrderGroups?.map((group) => {
                        const o = group[0]; // shared order-level fields (address, buyer, dates) - same for every line
                        const isOverdue = o.ship_by_date && !["Shipped", "Delivered", "Cancelled"].includes(o.status)
                            && new Date(o.ship_by_date) < new Date();

                        const totalQty = group.reduce((s, l) => s + (l.quantity || 0), 0);
                        const totalCharge = group.reduce((s, l) => s + (l.charge_total || 0), 0);
                        // Surface the "needs mark-shipped" alert as soon as ANY line
                        // in the group has a bought-but-unshipped label - even a
                        // single line left in that state needs the seller's
                        // attention, so this takes priority over Mixed/normal status
                        // rather than requiring every line to match.
                        const labelBoughtLine = group.find((l) => hasUnshippedLabel(l));
                        const anyLabelBought = !!labelBoughtLine;
                        const allSameStatus = group.every((l) => l.status === o.status);
                        const groupDisplayStatus = anyLabelBought
                            ? LABEL_BOUGHT_STATUS
                            : allSameStatus ? o.status : "Mixed";
                        const trackingSet = new Set(group.map((l) => l.tracking_number).filter(Boolean));
                        const soleTracking = trackingSet.size === 1 ? [...trackingSet][0] : null;
                        const buyerKey = o.buyer_name ? `${o.buyer_name}|${o.postal_code || ""}` : null;
                        const buyerDupeCount = buyerKey ? (buyerOrderCounts.get(buyerKey) || 0) : 0;

                        const isAcknowledgeable = group.some((l) => l.status === "Created");
                        const cancelRisk = group.some((l) => l.intent_to_cancel && ["Created", "Acknowledged"].includes(l.status));
                        return (
                            <div key={o.purchase_order_id}
                                className={`w-full rounded-xl shadow p-4 flex items-center gap-3 transition border ${cancelRisk
                                    ? "border-red-300 bg-red-50/50 hover:bg-red-50"
                                    : "border-transparent bg-white hover:bg-teal-50"
                                    }`}>
                                {isAcknowledgeable && (
                                    <input type="checkbox" checked={selectedPOs.has(o.purchase_order_id)}
                                        onChange={(e) => { e.stopPropagation(); toggleSelectPO(o.purchase_order_id); }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-4 h-4 accent-teal-600 flex-shrink-0" />
                                )}
                                <button onClick={() => setSelectedOrder(o)} className="flex-1 flex items-center gap-3 text-left min-w-0">
                                    {group.length > 1 ? (
                                        <OrderImageStack group={group} />
                                    ) : (
                                        <ProductImage src={o.image_url} />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="font-mono text-xs font-bold text-gray-500">{o.purchase_order_id}</p>
                                            {o.customer_order_id && o.customer_order_id !== o.purchase_order_id && (
                                                <span className="text-[10px] text-gray-300">· Order {o.customer_order_id}</span>
                                            )}
                                        </div>
                                        <p className="text-sm font-semibold text-gray-800 truncate" title={group.map((l) => l.product_name).join(", ")}>
                                            {truncateTitle(o.product_name)}
                                            {group.length > 1 && <span className="text-gray-400 font-normal"> and {group.length - 1} more item{group.length > 2 ? "s" : ""}</span>}
                                        </p>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-400 mt-0.5">
                                            {group.length === 1 && <span className="font-mono">{o.sku}</span>}
                                            <span className="font-bold text-black text-l">{group.length > 1 ? `${group.length} items, ` : ""}Qty {totalQty}</span>
                                            <span className="font-semibold text-gray-600">{money(totalCharge)}</span>
                                            {o.state && <span>{o.state} {o.postal_code}</span>}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-400 mt-0.5">
                                            <span>{o.order_date ? new Date(o.order_date).toLocaleDateString() : "-"}</span>
                                            {o.buyer_name && (
                                                <span className={buyerDupeCount > 1 ? "flex items-center gap-1" : ""}
                                                    title={buyerDupeCount > 1 ? `${buyerDupeCount} separate orders from this buyer at this ZIP` : undefined}>
                                                    {buyerDupeCount > 1 && <p className="text-amber-600 font-semibold">({buyerDupeCount} separate orders) </p>}
                                                    Buyer: {o.buyer_name}
                                                </span>
                                            )}
                                            {soleTracking && <span className="font-mono">Tracking: {soleTracking}{o.carrier ? ` (${o.carrier})` : ""}</span>}
                                            {o.ship_by_date && !["Shipped", "Delivered", "Cancelled"].includes(o.status) && (
                                                <span className={`font-semibold ${isOverdue ? "text-red-500" : "text-orange-500"}`}>
                                                    {isOverdue ? "Overdue - " : "Ship by "}{new Date(o.ship_by_date).toLocaleDateString()}
                                                </span>
                                            )}
                                            {!["Shipped", "Delivered", "Cancelled"].includes(o.status) && (
                                                <LoadDeliverByButton purchaseOrderId={o.purchase_order_id} />
                                            )}
                                            {group.length === 1 && (
                                                <LoadIncentiveButton sku={o.sku} soldPrice={o.unit_price ?? (o.quantity ? (o.charge_total || 0) / o.quantity : null)} />
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {cancelRisk && (
                                            <span title="Customer requested cancellation - do not ship"
                                                className="flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                                                🔴 Customer requested cancellation
                                            </span>
                                        )}
                                        <PackingSlipButton order={o} lines={group} />
                                        {o.status === "Shipped" && (
                                            <DownloadLabelInlineButton purchaseOrderId={o.purchase_order_id} lineNumber={o.line_number} />
                                        )}
                                        {anyLabelBought && (
                                            <DownloadSwwLabelButton carrier={labelBoughtLine.sww_label_carrier} trackingNumber={labelBoughtLine.sww_label_tracking_number} />
                                        )}
                                        <StatusBadge status={groupDisplayStatus} />
                                        <ChevronRight size={16} className="text-gray-300" />
                                    </div>
                                </button>
                            </div>
                        );
                    })
                )}
            </div>

            {total > 0 && (
                <div className="flex items-center justify-between mt-5">
                    <p className="text-xs text-gray-400">Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} of {total}</p>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-50 transition disabled:opacity-40">Previous</button>
                        <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
                        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-50 transition disabled:opacity-40">Next</button>
                    </div>
                </div>
            )}

            {selectedOrder && <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} onChanged={load} />}
            {pickListOpen && <PickListModal onClose={() => setPickListOpen(false)} />}
        </div>
    );
};

export default OrdersPage;